/**
 * MockInterceptor — Camino feliz para desarrollo sin backend.
 *
 * ACTIVAR:  environment.useMocks = true  (environment.ts, default dev)
 * DESACTIVAR: environment.useMocks = false → tráfico real al backend
 *
 * Credenciales demo:
 *   admin@fuga.com / Admin123!         → ADMIN_FLEET
 *   superadmin@fuga.com / Admin123!    → SUPER_ADMIN (requiere MFA, code: 123456)
 *   user@fuga.com / User123!           → STANDARD_USER
 */

import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '@env/environment';
import { PermissionCode } from '@core/navigation/navigation.models';

// ── JWT helpers ───────────────────────────────────────────────────────────────

function mockJwt(claims: Record<string, unknown>): string {
  const encode = (obj: object) => btoa(JSON.stringify(obj)).replace(/=+$/, '');
  return `${encode({ alg: 'HS256' })}.${encode(claims)}.mock`;
}

const IAT = Math.floor(Date.now() / 1000);
const EXP = 9_999_999_999;

const ADMIN_JWT = mockJwt({
  sub: 'mock-admin-001',
  tenant_id: 'mock-tenant-001',
  role: 'ADMIN_FLEET',
  country_code: 'GT',
  locale: 'es-GT',
  timezone: 'America/Guatemala',
  currency: 'GTQ',
  exp: EXP,
  iat: IAT,
});

/**
 * El `sub` del super admin del mock es un UUID como los reales: la pantalla de
 * super admins compara `auth.me().userId` con el `id` de cada fila para bloquear
 * las acciones sobre uno mismo, y un `sub` no-UUID haría que esa regla nunca se
 * pudiera ver en dev (además de no pasar el Zod del contrato).
 */
const SUPER_ADMIN_SELF_ID = '019efb00-0000-7000-8000-00000000f001';

const SUPER_JWT = mockJwt({
  sub: SUPER_ADMIN_SELF_ID,
  role: 'SUPER_ADMIN',
  locale: 'es-GT',
  exp: EXP,
  iat: IAT,
});

const USER_JWT = mockJwt({
  sub: 'mock-user-001',
  tenant_id: 'mock-tenant-001',
  role: 'STANDARD_USER',
  locale: 'es-GT',
  exp: EXP,
  iat: IAT,
});

// STATION_BASIC: M16 se aísla por estación → station_id en el JWT, tenant_id null.
const STATION_BASIC_USER_ID = '019efb00-0000-7000-8000-0000000000a1';
const STATION_ID = '019efb00-0000-7000-8000-000000000001';
const STATION_JWT = mockJwt({
  sub: STATION_BASIC_USER_ID,
  station_id: STATION_ID,
  role: 'STATION_BASIC',
  country_code: 'GT',
  locale: 'es-GT',
  timezone: 'America/Guatemala',
  currency: 'GTQ',
  exp: EXP,
  iat: IAT,
});

const pair = (token: string) => ({
  access_token: token,
  refresh_token: `mock-refresh-${Date.now()}`,
  expires_in: 900,
  token_type: 'Bearer' as const,
});

/** Decodifica el payload de un mockJwt (btoa de JSON) → claims. */
function decodeMockClaims(token: string): Record<string, unknown> {
  try {
    return JSON.parse(atob(token.split('.')[1])) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** Nombre demo por rol para el `fullName` de `/auth/me`. */
const MOCK_FULLNAME: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin (mock)',
  ADMIN_FLEET: 'Diego Cortés',
  STANDARD_USER: 'Usuario Estándar',
  STATION_BASIC: 'Encargado Estación',
  DISPATCHER: 'Despachador',
  DRIVER: 'Conductor',
};

/** Construye la respuesta de `GET /v1/auth/me` a partir del último token emitido. */
function mockMe(token: string): Record<string, unknown> {
  const c = decodeMockClaims(token);
  const role = String(c['role'] ?? 'ADMIN_FLEET');
  const tenantId = (c['tenant_id'] as string | undefined) ?? null;
  const stationId = (c['station_id'] as string | undefined) ?? null;
  const countryCode = (c['country_code'] as string | undefined) ?? 'GT';
  const locale = (c['locale'] as string | undefined) ?? 'es-GT';
  const timezone = (c['timezone'] as string | undefined) ?? 'America/Guatemala';
  const currency = (c['currency'] as string | undefined) ?? 'GTQ';
  return {
    userId: String(c['sub'] ?? 'mock-user'),
    email: `${role.toLowerCase()}@fuga.com`,
    fullName: MOCK_FULLNAME[role] ?? role,
    avatarFileId: null,
    role,
    tenantId,
    tenant: tenantId
      ? {
          id: tenantId,
          commercialName: 'Acme Logística (mock)',
          subdomain: 'acme',
          countryCode,
          currencyCode: currency,
          timezone,
          locale,
        }
      : null,
    stationId,
    station: stationId
      ? {
          id: stationId,
          code: 'GT-001',
          name: 'Estación Centro',
          countryCode,
          timezone,
        }
      : null,
    driverId: null,
    countryCode,
    locale,
    mfaEnabled: role === 'SUPER_ADMIN',
    mustChangePassword: false,
  };
}

/**
 * FGA-90: temporal cripto-aleatoria de 16 chars sin caracteres ambiguos
 * (0/O/1/l/I) para dictarla/transcribirla por canal seguro.
 */
function genTempPassword(): string {
  // Incluye símbolos: RN-05 exige que la temporal cumpla la política, y una
  // temporal solo alfanumérica sería rechazada por el backend real.
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%&*+=?';
  const rnd = new Uint32Array(16);
  crypto.getRandomValues(rnd);
  let out = '';
  for (let i = 0; i < 16; i++) out += alphabet[rnd[i] % alphabet.length];
  return mockPolicyViolation(out) ? genTempPassword() : out;
}

/** Espejo de la política por defecto del backend (`tenant_settings`). */
const MOCK_PASSWORD_POLICY = {
  min_length: 12,
  require_complexity: true,
  history_size: 5,
};

/**
 * Espejo de `PasswordPolicyService.validate` del backend. Devuelve el ENUM de
 * violación (no un mensaje) para que el mock ejercite el mismo contrato.
 */
function mockPolicyViolation(candidate: string): 'TOO_SHORT' | 'MISSING_COMPLEXITY' | null {
  if (candidate.length < MOCK_PASSWORD_POLICY.min_length) return 'TOO_SHORT';
  const complex =
    /[a-z]/.test(candidate) &&
    /[A-Z]/.test(candidate) &&
    /[0-9]/.test(candidate) &&
    /[^A-Za-z0-9]/.test(candidate);
  return MOCK_PASSWORD_POLICY.require_complexity && !complex ? 'MISSING_COMPLEXITY' : null;
}

// ── Mock data ─────────────────────────────────────────────────────────────────

const VEHICLES = [
  {
    id: 'v001',
    plate: 'P-123ABC',
    vin: 'JN1CV6EK1FM860001',
    brand: 'Nissan',
    model: 'Versa',
    year: 2015,
    vehicleClass: 'sedan',
    fuelTypeCode: 'REGULAR',
    status: 'active',
    countryCode: 'GT',
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-06-01T08:00:00Z',
  },
  {
    id: 'v002',
    plate: 'M-456DEF',
    vin: null,
    brand: 'Toyota',
    model: 'Hilux',
    year: 2019,
    vehicleClass: 'pickup',
    fuelTypeCode: 'DIESEL',
    status: 'maintenance',
    countryCode: 'GT',
    createdAt: '2026-02-10T09:00:00Z',
    updatedAt: '2026-05-20T12:00:00Z',
  },
  {
    id: 'v003',
    plate: '1234-XYZ',
    vin: '1HGBH41JXMN109186',
    brand: 'Honda',
    model: 'Civic',
    year: 2020,
    vehicleClass: 'sedan',
    fuelTypeCode: 'PREMIUM',
    status: 'active',
    countryCode: 'CR',
    createdAt: '2026-03-05T14:00:00Z',
    updatedAt: '2026-06-05T16:00:00Z',
  },
];

/**
 * Fuente de captura de posición por vehículo (HU position-source). Estado
 * mutable para que el PATCH del panel se refleje en el GET siguiente.
 *  - v001: device vinculado y conductor asignado (caso feliz).
 *  - v002: sin device, con conductor → solo móvil; elegir GPS devuelve 422.
 *  - v003: sin device y sin conductor → los dos avisos a la vez.
 */
const POSITION_SOURCE_STATE: Record<
  string,
  {
    positionSource: string;
    lastPositionSource: string | null;
    gpsDeviceId: string | null;
    activeAssignment: { driverId: string; driverName: string; assignedFrom: string } | null;
  }
> = {
  v001: {
    positionSource: 'gps_device',
    lastPositionSource: 'device',
    gpsDeviceId: 'aaaa1111-1111-1111-1111-111111111111',
    activeAssignment: {
      driverId: 'd001',
      driverName: 'Carlos Ramírez',
      assignedFrom: '2026-07-01T14:00:00Z',
    },
  },
  v002: {
    positionSource: 'driver_phone',
    lastPositionSource: 'mobile',
    gpsDeviceId: null,
    activeAssignment: {
      driverId: 'd002',
      driverName: 'María López',
      assignedFrom: '2026-07-15T08:30:00Z',
    },
  },
  v003: {
    positionSource: 'gps_device',
    lastPositionSource: null,
    gpsDeviceId: null,
    activeAssignment: null,
  },
};

const positionSourceConfig = (id: string) => {
  const state = (POSITION_SOURCE_STATE[id] ??= {
    positionSource: 'gps_device',
    lastPositionSource: 'device',
    gpsDeviceId: 'aaaa9999-9999-9999-9999-999999999999',
    activeAssignment: null,
  });
  const warnings: string[] = [];
  if (!state.gpsDeviceId) warnings.push('GPS_DEVICE_NOT_LINKED');
  if (!state.activeAssignment) warnings.push('NO_ACTIVE_DRIVER_ASSIGNMENT');
  return { vehicleId: id, ...state, warnings, updatedAt: now() };
};

const vehicleDetail = (id: string) => {
  const base = VEHICLES.find((v) => v.id === id) ?? VEHICLES[0];
  return {
    ...base,
    color: 'Blanco',
    tankCapacity: 13,
    tankCapacityUnit: 'gallon',
    hasFuelSensor: true,
    initialOdometer: 120000,
    currentOdometer: 145850,
    odometerUnit: 'km',
    nfcTagId: null,
    qrTagValue: null,
  };
};

const DOCS = [
  {
    id: 'doc001',
    vehicleId: 'v001',
    documentType: 'circulation_card',
    documentNumber: 'TC-2026-12345',
    fileId: 'file001',
    issuedAt: '2025-08-15',
    expiresAt: '2027-08-15',
    issuingEntity: 'SAT Guatemala',
    notes: 'Tarjeta de circulación 2025-2027',
    isCurrent: true,
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-01-15T10:00:00Z',
  },
  {
    id: 'doc002',
    vehicleId: 'v001',
    documentType: 'insurance',
    documentNumber: 'SEG-2026-9876',
    fileId: 'file002',
    issuedAt: '2026-01-01',
    expiresAt: new Date(Date.now() + 20 * 86_400_000).toISOString().slice(0, 10),
    issuingEntity: 'Aseguradora Nacional',
    notes: null,
    isCurrent: true,
    createdAt: '2026-01-15T10:00:00Z',
    updatedAt: '2026-01-15T10:00:00Z',
  },
];

const STATIONS = [
  {
    id: 'sta001',
    code: 'GAS-ACME-Z01',
    name: 'Gasolinera Acme Zona 1',
    countryCode: 'GT',
    status: 'active',
    centroid: { lat: 14.60435, lng: -90.51795 },
    createdAt: '2026-01-01T00:00:00Z',
    updatedAt: '2026-06-01T00:00:00Z',
  },
  {
    id: 'sta002',
    code: 'GAS-BETA-Z05',
    name: 'Gasolinera Beta Zona 5',
    countryCode: 'GT',
    status: 'active',
    centroid: { lat: 14.62, lng: -90.523 },
    createdAt: '2026-02-01T00:00:00Z',
    updatedAt: '2026-06-01T00:00:00Z',
  },
];

const stationDetail = (id: string) => ({
  ...(STATIONS.find((s) => s.id === id) ?? STATIONS[0]),
  legalName: 'Combustibles Acme S.A.',
  taxId: '12345678',
  address: 'Avenida Reforma 12-45, Zona 9, Guatemala',
  googlePlaceId: null,
  polygon: null,
  gpsToleranceRadiusMeters: 30,
  hdopThreshold: 5.0,
  graceSeconds: 60,
  pumpControllerBrand: 'gilbarco',
  pumpControllerModel: 'Encore 700',
  pumpControllerConfig: null,
  manager: { name: 'Carlos Manager', phone: '+50255559876', email: 'manager@acme.gt' },
  islands: [
    {
      id: 'isl001',
      code: 'I1',
      name: 'Isla Norte',
      pumps: [
        {
          id: 'pmp001',
          code: 'B1',
          controllerId: '1',
          nfcTagId: null,
          status: 'active',
          fuelTypes: [
            { id: 'ft001', code: 'REGULAR', name: 'Regular' },
            { id: 'ft002', code: 'PREMIUM', name: 'Premium' },
          ],
        },
        {
          id: 'pmp002',
          code: 'B2',
          controllerId: '2',
          nfcTagId: null,
          status: 'active',
          fuelTypes: [{ id: 'ft003', code: 'DIESEL', name: 'Diésel' }],
        },
      ],
    },
  ],
  schedules: [
    { dayOfWeek: 1, windows: [{ opensAt: '06:00', closesAt: '22:00', isOvernight: false }] },
    { dayOfWeek: 2, windows: [{ opensAt: '06:00', closesAt: '22:00', isOvernight: false }] },
    { dayOfWeek: 3, windows: [{ opensAt: '06:00', closesAt: '22:00', isOvernight: false }] },
    { dayOfWeek: 4, windows: [{ opensAt: '06:00', closesAt: '22:00', isOvernight: false }] },
    { dayOfWeek: 5, windows: [{ opensAt: '06:00', closesAt: '22:00', isOvernight: false }] },
    { dayOfWeek: 6, windows: [{ opensAt: '07:00', closesAt: '20:00', isOvernight: false }] },
    { dayOfWeek: 0, windows: [] },
  ],
});

const AUDIT_LOGS = [
  {
    id: 'al001',
    tenantId: 'mock-tenant-001',
    tableSchema: 'fleet',
    tableName: 'vehicles',
    recordId: 'v001',
    action: 'UPDATE',
    customAction: null,
    actorUserId: 'mock-admin-001',
    actorUserType: 'fleet_admin',
    actorIp: '190.86.234.12',
    actorUserAgent: 'Mozilla/5.0 (FGA Demo)',
    requestId: 'req-001',
    sessionId: 'ses-001',
    changedColumns: ['current_odometer', 'updated_at'],
    recordedAt: '2026-06-08T10:00:00Z',
  },
  {
    id: 'al002',
    tenantId: 'mock-tenant-001',
    tableSchema: 'iam',
    tableName: 'login_events',
    recordId: null,
    action: 'CUSTOM',
    customAction: 'login_success',
    actorUserId: 'mock-admin-001',
    actorUserType: 'fleet_admin',
    actorIp: '190.86.234.12',
    actorUserAgent: 'Mozilla/5.0 (FGA Demo)',
    requestId: 'req-002',
    sessionId: 'ses-001',
    changedColumns: null,
    recordedAt: '2026-06-08T09:55:00Z',
  },
  {
    id: 'al003',
    tenantId: 'mock-tenant-001',
    tableSchema: 'fleet',
    tableName: 'vehicle_documents',
    recordId: 'doc001',
    action: 'INSERT',
    customAction: null,
    actorUserId: 'mock-admin-001',
    actorUserType: 'fleet_admin',
    actorIp: '190.86.234.12',
    actorUserAgent: 'Mozilla/5.0 (FGA Demo)',
    requestId: 'req-003',
    sessionId: 'ses-001',
    changedColumns: null,
    recordedAt: '2026-06-08T09:50:00Z',
  },
  {
    id: 'al004',
    tenantId: 'mock-tenant-001',
    tableSchema: 'fleet',
    tableName: 'vehicles',
    recordId: 'v002',
    action: 'INSERT',
    customAction: null,
    actorUserId: 'mock-admin-001',
    actorUserType: 'fleet_admin',
    actorIp: '190.86.234.12',
    actorUserAgent: 'Mozilla/5.0 (FGA Demo)',
    requestId: 'req-004',
    sessionId: 'ses-001',
    changedColumns: null,
    recordedAt: '2026-06-07T16:30:00Z',
  },
  {
    id: 'al005',
    tenantId: 'mock-tenant-001',
    tableSchema: 'fleet',
    tableName: 'vehicles',
    recordId: 'v001',
    action: 'UPDATE',
    customAction: null,
    actorUserId: 'mock-admin-001',
    actorUserType: 'fleet_admin',
    actorIp: '190.86.234.12',
    actorUserAgent: 'Mozilla/5.0 (FGA Demo)',
    requestId: 'req-005',
    sessionId: 'ses-002',
    changedColumns: ['status', 'updated_at'],
    recordedAt: '2026-06-07T14:00:00Z',
  },
];

// Contrato real de conductores (M17): camelCase, con nationalId/qrTagValue/nfcTagId,
// sin countryCode ni firstName/lastName.
const DRIVERS = [
  {
    id: 'drv001',
    nationalId: '1234567890101',
    igssNumber: '12345678',
    fullName: 'Carlos Pérez',
    phone: '+50255550000',
    email: 'carlos.perez@acme.gt',
    photoFileId: null,
    birthdate: '1988-04-12',
    emergencyContactName: 'Ana Pérez',
    emergencyContactPhone: '+50255550001',
    usualWorkStart: '07:00:00',
    usualWorkEnd: '17:00:00',
    workDaysOfWeek: [1, 2, 3, 4, 5],
    nfcTagId: null,
    qrTagValue: 'eyJkcml2ZXJJZCI6ImRydjAwMSJ9.mock-qr-signature-001',
    status: 'active',
    createdAt: '2026-01-10T10:00:00Z',
    updatedAt: '2026-05-15T08:00:00Z',
  },
  {
    id: 'drv002',
    nationalId: '2345678901202',
    igssNumber: null,
    fullName: 'María García',
    phone: '+50255550010',
    email: 'maria.garcia@acme.gt',
    photoFileId: null,
    birthdate: '1992-11-03',
    emergencyContactName: 'Luis García',
    emergencyContactPhone: '+50255550011',
    usualWorkStart: '06:00:00',
    usualWorkEnd: '14:00:00',
    workDaysOfWeek: [1, 2, 3, 4, 5, 6],
    nfcTagId: '04AABBCCDD',
    qrTagValue: 'eyJkcml2ZXJJZCI6ImRydjAwMiJ9.mock-qr-signature-002',
    status: 'active',
    createdAt: '2026-02-20T09:00:00Z',
    updatedAt: '2026-06-01T10:00:00Z',
  },
  {
    id: 'drv003',
    nationalId: '3456789012303',
    igssNumber: '87654321',
    fullName: 'Jorge Ramírez',
    phone: '+50255550020',
    email: null,
    photoFileId: null,
    birthdate: '1985-07-21',
    emergencyContactName: null,
    emergencyContactPhone: null,
    usualWorkStart: null,
    usualWorkEnd: null,
    workDaysOfWeek: [1, 2, 3, 4, 5],
    nfcTagId: null,
    qrTagValue: null,
    status: 'inactive',
    createdAt: '2026-03-01T11:00:00Z',
    updatedAt: '2026-04-10T14:00:00Z',
  },
];

const DRIVER_LICENSES: Record<string, object[]> = {
  drv001: [
    {
      id: 'lic001',
      driverId: 'drv001',
      licenseNumber: 'B-2026-123456',
      licenseType: 'B',
      issuedAt: '2024-06-01',
      expiresAt: '2028-06-01',
      issuingCountry: 'GT',
      fileId: null,
      isCurrent: true,
      createdAt: '2026-06-03T11:50:00Z',
      updatedAt: '2026-06-03T11:50:00Z',
    },
  ],
  drv002: [
    {
      id: 'lic002',
      driverId: 'drv002',
      licenseNumber: 'CR-B-987654',
      licenseType: 'B',
      issuedAt: '2022-09-15',
      expiresAt: new Date(Date.now() + 20 * 86_400_000).toISOString().slice(0, 10),
      issuingCountry: 'CR',
      fileId: null,
      isCurrent: true,
      createdAt: '2026-06-03T11:50:00Z',
      updatedAt: '2026-06-03T11:50:00Z',
    },
  ],
};

const DRIVER_ASSIGNMENTS: Record<string, object[]> = {
  drv001: [
    {
      id: 'asgn001',
      driverId: 'drv001',
      vehicleId: 'v001',
      assignedFrom: '2026-01-20T10:00:00Z',
      assignedTo: null,
      notes: null,
      createdAt: '2026-01-20T10:00:00Z',
      updatedAt: '2026-01-20T10:00:00Z',
    },
  ],
};

// ── Tenants mock DB (mutable for POST) ────────────────────────────────────────

const MOCK_TENANTS_DB: Record<string, unknown>[] = [
  {
    id: 'ten001',
    legalName: 'Acme Logística S.A.',
    commercialName: 'Acme Logística',
    taxId: '12345672',
    countryCode: 'GT',
    currencyCode: 'GTQ',
    timezone: 'America/Guatemala',
    locale: 'es-GT',
    subdomain: 'acme',
    planCode: 'pilot',
    status: 'active',
    fugaAccountManagerId: null,
    notes: null,
    onboardedAt: '2026-05-15T10:00:00Z',
    suspendedAt: null,
    suspendedReason: null,
    terminatedAt: null,
    createdAt: '2026-05-15T09:00:00Z',
    updatedAt: '2026-05-15T10:00:00Z',
  },
  {
    id: 'ten002',
    legalName: 'Beta Transport S.R.L.',
    commercialName: 'Beta Transport',
    taxId: '3-101-654321',
    countryCode: 'CR',
    currencyCode: 'CRC',
    timezone: 'America/Costa_Rica',
    locale: 'es-CR',
    subdomain: 'beta-transport',
    planCode: 'pilot',
    status: 'provisioning',
    fugaAccountManagerId: null,
    notes: 'Cliente referido por partner CR',
    onboardedAt: null,
    suspendedAt: null,
    suspendedReason: null,
    terminatedAt: null,
    createdAt: '2026-06-10T14:00:00Z',
    updatedAt: '2026-06-10T14:00:00Z',
  },
  {
    id: 'ten003',
    legalName: 'Gamma Flota Corp.',
    commercialName: 'Gamma Flota',
    taxId: '98765430',
    countryCode: 'GT',
    currencyCode: 'GTQ',
    timezone: 'America/Guatemala',
    locale: 'es-GT',
    subdomain: 'gamma-flota',
    planCode: 'pilot',
    status: 'suspended',
    fugaAccountManagerId: null,
    notes: null,
    onboardedAt: '2026-03-01T08:00:00Z',
    suspendedAt: '2026-06-01T00:00:00Z',
    suspendedReason: 'Cliente no pagó factura mayo 2026',
    terminatedAt: null,
    createdAt: '2026-03-01T07:00:00Z',
    updatedAt: '2026-06-01T00:00:00Z',
  },
];

// After ~3 seconds, a 'provisioning' tenant transitions to 'active' in the mock
const PROVISIONING_TIMERS = new Map<string, ReturnType<typeof setTimeout>>();

function scheduleMockActivation(id: string): void {
  if (PROVISIONING_TIMERS.has(id)) return;
  const timer = setTimeout(() => {
    const tenant = MOCK_TENANTS_DB.find((t) => t['id'] === id);
    if (tenant && tenant['status'] === 'provisioning') {
      tenant['status'] = 'active';
      tenant['onboardedAt'] = new Date().toISOString();
      tenant['updatedAt'] = new Date().toISOString();
    }
    PROVISIONING_TIMERS.delete(id);
  }, 5000);
  PROVISIONING_TIMERS.set(id, timer);
}

const MFA_ENROLL = {
  secret: 'JBSWY3DPEHPK3PXP',
  otpauth_url: 'otpauth://totp/FUGA:superadmin@fuga.com?secret=JBSWY3DPEHPK3PXP&issuer=FUGA',
  backup_codes: [
    'A1B2-C3D4',
    'E5F6-G7H8',
    'I9J0-K1L2',
    'M3N4-O5P6',
    'Q7R8-S9T0',
    'U1V2-W3X4',
    'Y5Z6-A7B8',
    'C9D0-E1F2',
    'G3H4-I5J6',
    'K7L8-M9N0',
  ],
};

// ── Geofences (FGA-16 / M05) ──────────────────────────────────────────────────

const SAMPLE_POLYGON = {
  type: 'Polygon',
  coordinates: [
    [
      [-90.52, 14.6],
      [-90.5, 14.6],
      [-90.5, 14.62],
      [-90.52, 14.62],
      [-90.52, 14.6],
    ],
  ],
};

function makeGeofence(o: Record<string, unknown> = {}): Record<string, unknown> {
  return {
    id: (o['id'] as string) ?? crypto.randomUUID(),
    name: (o['name'] as string) ?? 'Geocerca',
    description: (o['description'] as string) ?? null,
    categoryCode: (o['categoryCode'] as string) ?? 'operational',
    shapeType: 'polygon',
    geometry: o['geometry'] ?? SAMPLE_POLYGON,
    centroid: { lat: 14.61, lng: -90.51 },
    areaSqMeters: 2469134.5,
    effectiveFrom: (o['effectiveFrom'] as string) ?? null,
    effectiveTo: (o['effectiveTo'] as string) ?? null,
    validityWindow: o['validityWindow'] ?? null,
    countryCode: 'GT',
    isActive: o['isActive'] === undefined ? true : Boolean(o['isActive']),
    createdAt: now(),
    updatedAt: now(),
  };
}

const GEOFENCES: Record<string, unknown>[] = [
  makeGeofence({
    id: '019ef62b-1111-7000-8000-000000000001',
    name: 'Estación Zona 4',
    categoryCode: 'operational',
    validityWindow: { windows: [{ days: [1, 2, 3, 4, 5], start: '06:00', end: '18:00' }] },
  }),
  makeGeofence({
    id: '019ef62b-1111-7000-8000-000000000002',
    name: 'Zona prohibida Norte',
    categoryCode: 'forbidden_zone',
    isActive: false,
  }),
  makeGeofence({
    id: '019ef62b-1111-7000-8000-000000000003',
    name: 'Anti-fraude Bodega Central',
    categoryCode: 'anti_fraud',
  }),
];

// ── Credit rules (FGA-17 / M04) ───────────────────────────────────────────────

const FUEL_TYPES = [
  {
    id: '019ef62b-aaaa-7000-8000-000000000001',
    code: 'REGULAR',
    name: 'Regular',
    description: null,
    isActive: true,
  },
  {
    id: '019ef62b-aaaa-7000-8000-000000000002',
    code: 'DIESEL',
    name: 'Diésel',
    description: null,
    isActive: true,
  },
  {
    id: '019ef62b-aaaa-7000-8000-000000000003',
    code: 'PREMIUM',
    name: 'Premium',
    description: null,
    isActive: true,
  },
  {
    id: '019ef62b-aaaa-7000-8000-000000000009',
    code: 'KEROSENE',
    name: 'Kerosene',
    description: 'Descontinuado',
    isActive: false,
  },
];

const VEHICLE_GROUPS = [
  { id: '019ef777-0002-7000-8000-000000000abc', name: 'Ruta Sur' },
  { id: '019ef777-0002-7000-8000-000000000abd', name: 'Ruta Norte' },
];

/** vehicleId que el mock resuelve como "sin regla" (resolvedFrom: 'none'). */
const VEHICLE_NO_RULE = '019ef888-0003-7000-8000-000000000fff';

function makeCreditRule(o: Record<string, unknown> = {}): Record<string, unknown> {
  const scopeType = (o['scopeType'] as string) ?? 'fleet';
  return {
    id: (o['id'] as string) ?? crypto.randomUUID(),
    name: (o['name'] as string) ?? 'Regla de crédito',
    scopeType,
    scopeId: scopeType === 'fleet' ? null : ((o['scopeId'] as string) ?? null),
    dailyLimitAmount: o['dailyLimitAmount'] ?? null,
    dailyLimitVolume: o['dailyLimitVolume'] ?? null,
    monthlyLimitAmount: o['monthlyLimitAmount'] ?? null,
    monthlyLimitVolume: o['monthlyLimitVolume'] ?? null,
    perTransactionLimitAmount: o['perTransactionLimitAmount'] ?? null,
    perTransactionLimitVolume: o['perTransactionLimitVolume'] ?? null,
    maxPricePerUnit: o['maxPricePerUnit'] ?? null,
    // FGA-32c §4: coherencia both-or-neither. Sin precio no hay unidad; con precio
    // y sin unidad explícita, el backend asume `gallon`.
    maxPriceUnit:
      o['maxPricePerUnit'] === undefined || o['maxPricePerUnit'] === null
        ? null
        : ((o['maxPriceUnit'] as string) ?? 'gallon'),
    currencyCode: (o['currencyCode'] as string) ?? 'GTQ',
    allowedFuelTypeIds: o['allowedFuelTypeIds'] ?? null,
    effectiveFrom: (o['effectiveFrom'] as string) ?? now(),
    effectiveTo: (o['effectiveTo'] as string) ?? null,
    isActive: o['isActive'] === undefined ? true : Boolean(o['isActive']),
    createdAt: now(),
    updatedAt: now(),
  };
}

const CREDIT_RULES: Record<string, unknown>[] = [
  makeCreditRule({
    id: '019ef999-0001-7000-8000-000000000001',
    name: 'Regla flota - 50 gal Regular',
    scopeType: 'fleet',
    dailyLimitVolume: 50,
    maxPricePerUnit: 35,
    allowedFuelTypeIds: ['019ef62b-aaaa-7000-8000-000000000001'],
  }),
  makeCreditRule({
    id: '019ef999-0002-7000-8000-000000000002',
    name: 'Ruta sur - 30 gal Diésel',
    scopeType: 'group',
    scopeId: '019ef777-0002-7000-8000-000000000abc',
    dailyLimitVolume: 30,
    maxPricePerUnit: 37.85,
    maxPriceUnit: 'gallon',
    perTransactionLimitVolume: 25,
    allowedFuelTypeIds: ['019ef62b-aaaa-7000-8000-000000000002'],
  }),
  // FGA-32c: regla SIN techo de precio — reproduce el 422 NO_REFERENCE_PRICE y el
  // bloqueo del pre-flight (§2). Es el caso exactamente reportado en el doc.
  makeCreditRule({
    id: '019fc82f-1537-7b2c-9f1f-171887df11ed',
    name: '50 gal Regular',
    scopeType: 'fleet',
    dailyLimitVolume: 5,
    dailyLimitAmount: 5,
    allowedFuelTypeIds: ['019ef62b-aaaa-7000-8000-000000000001'],
  }),
];

// ── Credit balances / top-ups (FGA-18 / M04) ─────────────────────────────────
function makeTopUp(o: Record<string, unknown> = {}): Record<string, unknown> {
  const evidenceFileId = (o['evidenceFileId'] as string) ?? crypto.randomUUID();
  return {
    id: (o['id'] as string) ?? crypto.randomUUID(),
    amount: Number(o['amount'] ?? 0),
    currencyCode: (o['currencyCode'] as string) ?? 'GTQ',
    createdAt: now(),
    approvedByUserId: '019ef633-ff23-7000-8000-0000000000aa',
    approvedByName: 'Súper Admin Demo',
    paymentReference: (o['paymentReference'] as string) ?? 'TRF-MOCK',
    evidenceUrl: `/v1/files/${evidenceFileId}/download?t=mock-download-token`,
  };
}

const TOPUPS: Record<string, unknown>[] = [
  makeTopUp({
    id: '019ef9bb-2222-7000-8000-000000000333',
    amount: 50000,
    currencyCode: 'GTQ',
    paymentReference: 'TRF-99812',
  }),
];

// ── Dispatch (FGA-19) — recuerda los despachos ya cancelados para simular el
// 409 RESERVATION_TERMINAL_STATE en un 2º intento (idempotencia).
const CANCELED_DISPATCHES = new Set<string>();

// ── Reservas de despacho por vehículo (FGA-32 / M15 — Endpoint A) ─────────────
// @contract-assumed FGA-32 — solo el POST está documentado; el listado, la
// cancelación y el estado del store son asumidos para el mock.
const RESV_SEED_VEHICLE = '019ef62b-0000-7000-8000-0000000000a1';
const RESV_SEED_DRIVER = '019ef630-0000-7000-8000-0000000000b1';
// Sentinelas de error para el POST (por driver/vehículo).
const DRIVER_WITH_ACTIVE_RESERVATION = '019ef630-0000-7000-8000-0000000000ff';
const VEHICLE_INSUFFICIENT_BALANCE = '019ef62b-0000-7000-8000-0000000000fe';
// FGA-32b: POST sin driverId contra un vehículo sin asignación activa → 422.
const VEHICLE_NO_DRIVER = '019ef62b-0000-7000-8000-0000000000fd';
// FGA-32c §3: sentinelas de los errores nuevos/desglosados.
const VEHICLE_NO_PRICE = '019ef62b-0000-7000-8000-0000000000fc';
const VEHICLE_LIMIT_EXCEEDED = '019ef62b-0000-7000-8000-0000000000fb';
const VEHICLE_WALLET_EMPTY = '019ef62b-0000-7000-8000-0000000000fa';

/** Regla sin techo de precio, tal cual el ejemplo del doc FGA-32c. */
const NO_PRICE_RULE_REF = {
  id: '019fc82f-1537-7b2c-9f1f-171887df11ed',
  name: '50 gal Regular',
  scopeType: 'fleet',
  resolvedFrom: 'fleet',
};

function makeVehicleReservation(o: Record<string, unknown> = {}): Record<string, unknown> {
  const reservedVolume = Number(o['maxVolume'] ?? o['reservedVolume'] ?? 10);
  // FGA-32c §4: el hold se valora con el techo de la regla, ya convertido a la
  // unidad de la reserva. El mock usa el precio de la regla de grupo (37.85/gal).
  const referenceUnitPrice = 37.8541;
  return {
    reservationId: (o['reservationId'] as string) ?? crypto.randomUUID(),
    vehicleId: (o['vehicleId'] as string) ?? RESV_SEED_VEHICLE,
    vehiclePlate: (o['vehiclePlate'] as string) ?? 'P-123ABC',
    driverId: (o['driverId'] as string) ?? RESV_SEED_DRIVER,
    driverName: (o['driverName'] as string) ?? 'Juan Pérez',
    fuelTypeId: (o['fuelTypeId'] as string) ?? FUEL_TYPES[0].id,
    reservedVolume,
    unit: (o['unit'] as string) ?? 'gallon',
    status: (o['status'] as string) ?? 'active',
    currencyCode: (o['currencyCode'] as string) ?? 'GTQ',
    estimatedAmount: Number((reservedVolume * referenceUnitPrice).toFixed(2)),
    referenceUnitPrice,
    referencePriceUnit: (o['unit'] as string) ?? 'gallon',
    expiresAt: new Date(Date.now() + 24 * 60 * 60_000).toISOString(),
    createdAt: now(),
  };
}

// @contract-assumed FGA-32 — store en memoria del listado de reservas.
const VEHICLE_RESERVATIONS: Record<string, unknown>[] = [
  makeVehicleReservation({
    reservationId: '019ef6aa-0000-7000-8000-000000000001',
    vehicleId: RESV_SEED_VEHICLE,
    driverId: RESV_SEED_DRIVER,
    reservedVolume: 10,
  }),
];

// ── Dispatch authorize/override (FGA-21) ─────────────────────────────────────
function makeReservation(fuelDispatchId: string, volume: number): Record<string, unknown> {
  return {
    reservationId: crypto.randomUUID(),
    fuelDispatchId,
    status: 'active',
    reservedAmount: Number((volume * 5.7).toFixed(2)), // volumen * precio mock
    reservedVolume: volume,
    currencyCode: 'GTQ',
    expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
  };
}

function makeAuthorizeOutput(
  fuelDispatchId: string,
  volume: number,
  override: boolean,
): Record<string, unknown> {
  const reservation = makeReservation(fuelDispatchId, volume);
  return override
    ? {
        fuelDispatchId,
        overridden: true,
        geofenceValidationPassed: false,
        graceModeUsed: true,
        reservation,
      }
    : {
        fuelDispatchId,
        geofenceValidationPassed: true,
        vehicleHdopAtAuth: 2.1,
        graceModeUsed: false,
        reservation,
      };
}

// Ids de despacho del demo que provocan un rechazo concreto (resto → 200 OK).
const DISPATCH_AUTH_OUTCOMES: Record<string, MockResult> = {
  '019efa01-0000-7000-8000-000000000002': {
    status: 403,
    body: { code: 'GEOFENCE_OUT_OF_BOUNDS', message: 'Vehículo fuera de la geocerca.' },
  },
  '019efa01-0000-7000-8000-000000000003': {
    status: 422,
    body: { code: 'TELEMETRY_STALE', message: 'Telemetría desactualizada.' },
  },
};

// ── Shifts / turnos de estación (FGA-28 / M16) ───────────────────────────────
const FT_REGULAR = '019efb00-0000-7000-8000-0000000000f1';
const FT_DIESEL = '019efb00-0000-7000-8000-0000000000f2';
const ISLAND_NORTE = '019efb00-0000-7000-8000-000000000011';
const ISLAND_SUR = '019efb00-0000-7000-8000-000000000012';
const PUMP_B1 = '019efb00-0000-7000-8000-000000000111';
const PUMP_B2 = '019efb00-0000-7000-8000-000000000112';
const PUMP_B3 = '019efb00-0000-7000-8000-000000000113';
const DISPATCHER_1 = '019efb00-0000-7000-8000-0000000000d1';
const DISPATCHER_2 = '019efb00-0000-7000-8000-0000000000d2';

const RECONCILIATION_TOLERANCE_PCT = 0.5;

const STATION_TIMEZONE = 'America/Guatemala';

/** FGA-28 — `GET /v1/shifts/open-context`. `openShiftId` se resuelve en el handler. */
const OPEN_CONTEXT = {
  stationId: STATION_ID,
  stationTimezone: STATION_TIMEZONE,
  openShiftId: null as string | null,
  islands: [
    { islandId: ISLAND_NORTE, islandCode: 'N', islandName: 'Isla Norte' },
    { islandId: ISLAND_SUR, islandCode: 'S', islandName: 'Isla Sur' },
  ],
  pumps: [
    {
      pumpId: PUMP_B1,
      pumpCode: 'B1',
      islandId: ISLAND_NORTE,
      status: 'active',
      fuelTypeCodes: ['REGULAR'],
    },
    {
      pumpId: PUMP_B2,
      pumpCode: 'B2',
      islandId: ISLAND_NORTE,
      status: 'active',
      fuelTypeCodes: ['DIESEL'],
    },
    {
      pumpId: PUMP_B3,
      pumpCode: 'B3',
      islandId: ISLAND_SUR,
      status: 'active',
      fuelTypeCodes: ['REGULAR'],
    },
  ],
  dispatchers: [
    { userId: DISPATCHER_1, fullName: 'Pedro Despachador' },
    { userId: DISPATCHER_2, fullName: 'Lucía Despachadora' },
  ],
  fuelTypes: [
    { fuelTypeId: FT_REGULAR, code: 'REGULAR', name: 'Regular', lastPrice: null },
    { fuelTypeId: FT_DIESEL, code: 'DIESEL', name: 'Diésel', lastPrice: null },
  ],
};

const SHIFT_STORE = new Map<string, Record<string, unknown>>();

function makeOpenShift(body: Record<string, unknown>): Record<string, unknown> {
  const id = crypto.randomUUID();
  const assignments = ((body['assignments'] as Record<string, unknown>[]) ?? []).map((a) => ({
    id: crypto.randomUUID(),
    islandId: a['islandId'],
    dispatcherUserId: a['dispatcherUserId'],
    assignedAt: now(),
    unassignedAt: null,
  }));
  const readings = ((body['initialReadings'] as Record<string, unknown>[]) ?? []).map((r) => ({
    id: crypto.randomUUID(),
    pumpId: r['pumpId'],
    readingType: 'initial',
    readingValue: Number(r['readingValue']),
    fuelTypeId: (r['fuelTypeId'] as string) ?? null,
    fileId: (r['fileId'] as string) ?? null,
    notes: (r['notes'] as string) ?? null,
    recordedAt: now(),
  }));
  const openedAt = now();
  const fuelPrices = ((body['fuelPrices'] as Record<string, unknown>[]) ?? []).map((p) => ({
    id: crypto.randomUUID(),
    fuelTypeId: p['fuelTypeId'],
    unitPrice: Number(p['unitPrice']),
    currencyCode: (p['currencyCode'] as string) ?? 'GTQ',
    unit: (p['unit'] as string) ?? 'gallon',
    effectiveFrom: openedAt, // = openedAt del turno que lo capturó
    effectiveTo: null,
    shiftId: id,
  }));
  const detail = {
    id,
    stationId: STATION_ID,
    code: (body['code'] as string) ?? null,
    status: 'open',
    openedByUserId: STATION_BASIC_USER_ID,
    closedByUserId: null,
    openedAt,
    closedAt: null,
    reconciliationDiff: null,
    reconciliationNotes: null,
    assignments,
    readings,
    reconciliation: null,
    fuelPrices,
  };
  SHIFT_STORE.set(id, detail);
  return detail;
}

/**
 * FGA-29 — validación atómica de precios ANTES de abrir el turno (nada se persiste
 * si algo falla). El set operativo son los `fuelTypeId` de `OPEN_CONTEXT.fuelTypes`
 * (FT_REGULAR + FT_DIESEL).
 *
 * Sentinel de demo: como la UI ya envía el set correcto, para poder ejercitar los
 * branches por interfaz, si el `code` (folio) del turno contiene `FORCE_MISSING`
 * fuerza MISSING_FUEL_PRICE con todos los operativos, y `FORCE_NOTDISP` fuerza
 * FUEL_TYPE_NOT_DISPATCHED con los precios enviados.
 */
function validateFuelPrices(body: Record<string, unknown>): MockResult {
  const operative = new Set(OPEN_CONTEXT.fuelTypes.map((f) => f.fuelTypeId));
  const prices = (body['fuelPrices'] as Record<string, unknown>[]) ?? [];
  const priceIds = prices.map((p) => p['fuelTypeId'] as string);
  const sentinel = String(body['code'] ?? '');

  if (sentinel.includes('FORCE_NOTDISP')) {
    return { status: 422, body: { code: 'FUEL_TYPE_NOT_DISPATCHED', fuelTypeIds: priceIds } };
  }
  if (sentinel.includes('FORCE_MISSING')) {
    return {
      status: 422,
      body: { code: 'MISSING_FUEL_PRICE', missingFuelTypeIds: [...operative] },
    };
  }

  const notDispatched = priceIds.filter((id) => !operative.has(id));
  if (notDispatched.length) {
    return { status: 422, body: { code: 'FUEL_TYPE_NOT_DISPATCHED', fuelTypeIds: notDispatched } };
  }
  const missing = [...operative].filter((id) => !priceIds.includes(id));
  if (missing.length) {
    return { status: 422, body: { code: 'MISSING_FUEL_PRICE', missingFuelTypeIds: missing } };
  }
  return null;
}

function closeShift(
  detail: Record<string, unknown>,
  body: Record<string, unknown>,
): Record<string, unknown> {
  const tolerancePct = RECONCILIATION_TOLERANCE_PCT;
  const initial = detail['readings'] as Record<string, unknown>[];
  const finals = ((body['finalReadings'] as Record<string, unknown>[]) ?? []).map((r) => ({
    id: crypto.randomUUID(),
    pumpId: r['pumpId'],
    readingType: 'final',
    readingValue: Number(r['readingValue']),
    fuelTypeId: null,
    fileId: (r['fileId'] as string) ?? null,
    notes: (r['notes'] as string) ?? null,
    recordedAt: now(),
  }));
  const finalByPump = new Map(finals.map((f) => [f.pumpId, f.readingValue]));
  const lines = initial.map((r) => {
    const sold = Number(finalByPump.get(r['pumpId']) ?? 0) - Number(r['readingValue']);
    const dispatched = Number((sold * 0.998).toFixed(3)); // cuadre mock: ~0.2% diff
    return {
      pumpId: r['pumpId'],
      sold: Number(sold.toFixed(3)),
      dispatched,
      diff: Number((sold - dispatched).toFixed(3)),
    };
  });
  const dispatchedTotal = Number(lines.reduce((s, l) => s + l.dispatched, 0).toFixed(3));
  const diffTotal = Number(lines.reduce((s, l) => s + l.diff, 0).toFixed(3));
  const withinTolerance =
    dispatchedTotal === 0 ? true : (Math.abs(diffTotal) / dispatchedTotal) * 100 <= tolerancePct;
  const updated = {
    ...detail,
    status: withinTolerance ? 'closed' : 'reconciliation_pending',
    closedByUserId: STATION_BASIC_USER_ID,
    closedAt: now(),
    reconciliationDiff: diffTotal,
    reconciliationNotes: (body['reconciliationNotes'] as string) ?? null,
    readings: [...initial, ...finals],
    reconciliation: { tolerancePct, diffTotal, dispatchedTotal, withinTolerance, lines },
  };
  SHIFT_STORE.set(detail['id'] as string, updated);
  return updated;
}

/** FGA-30 — snapshot de transacciones cerradas del turno + totales del cuadre. */
function makeShiftTransactions(): Record<string, unknown> {
  const cc = 'GTQ';
  const items = [
    {
      id: '019efb00-0000-7000-8000-0000000000e1',
      closedAt: '2026-06-28T18:05:44.000Z',
      islandCode: 'Isla Norte',
      pumpCode: 'B1',
      fuelTypeName: 'Regular',
      dispensedVolume: 12.5,
      unitPriceSnapshot: 25.4,
      dispensedAmount: 317.5,
      currencyCode: cc,
      vehiclePlate: 'P-456DEF',
      driverName: 'Ana López',
      folio: 'F-000124',
    },
    {
      id: '019efb00-0000-7000-8000-0000000000e2',
      closedAt: '2026-06-28T17:41:02.000Z',
      islandCode: 'Isla Norte',
      pumpCode: 'B2',
      fuelTypeName: 'Diésel',
      dispensedVolume: 8.0,
      unitPriceSnapshot: 25.4,
      dispensedAmount: 203.2,
      currencyCode: cc,
      vehiclePlate: 'P-123ABC',
      driverName: 'Juan Pérez',
      folio: 'F-000123',
    },
    {
      id: '019efb00-0000-7000-8000-0000000000e3',
      closedAt: '2026-06-28T17:12:33.000Z',
      islandCode: 'Isla Sur',
      pumpCode: 'B3',
      fuelTypeName: 'Regular',
      dispensedVolume: 15.25,
      unitPriceSnapshot: 22.0,
      dispensedAmount: 335.5,
      currencyCode: cc,
      vehiclePlate: 'C-987XYZ',
      driverName: 'María Gómez',
      folio: null,
    },
  ];
  return {
    items,
    totals: {
      totalVolume: 35.75,
      totalAmount: 856.2,
      transactionsCount: items.length,
      currencyCode: cc,
    },
    nextCursor: null,
    stationTimezone: STATION_TIMEZONE,
  };
}

function shiftListItem(d: Record<string, unknown>): Record<string, unknown> {
  return {
    id: d['id'],
    stationId: d['stationId'],
    code: d['code'],
    status: d['status'],
    openedByUserId: d['openedByUserId'],
    closedByUserId: d['closedByUserId'],
    openedAt: d['openedAt'],
    closedAt: d['closedAt'],
    reconciliationDiff: d['reconciliationDiff'],
  };
}

// ── Super admins (back-office) ───────────────────────────────────────────────
/** El account manager del mock: su borrado devuelve 409 con los tenants a reasignar. */
const SUPER_ADMIN_MANAGER_ID = '019efb00-0000-7000-8000-00000000f003';

const SUPER_ADMINS: Record<string, unknown>[] = [
  {
    id: SUPER_ADMIN_SELF_ID,
    email: 'superadmin@fuga.com',
    fullName: 'Super Admin (tú)',
    phone: null,
    locale: 'es-GT',
    isActive: true,
    mfaEnabled: true,
    lastLoginAt: now(),
    createdAt: '2026-01-15T10:00:00.000Z',
  },
  {
    id: '019efb00-0000-7000-8000-00000000f002',
    email: 'ana.soporte@fuga.com',
    fullName: 'Ana Soporte',
    phone: '+50760000001',
    locale: 'es-GT',
    isActive: true,
    mfaEnabled: false,
    // Sin MFA y sin ingresos: el caso "probablemente no usó su temporal".
    lastLoginAt: null,
    createdAt: '2026-08-10T09:00:00.000Z',
  },
  {
    id: SUPER_ADMIN_MANAGER_ID,
    email: 'carlos.cuentas@fuga.com',
    fullName: 'Carlos Cuentas',
    phone: null,
    locale: 'es-GT',
    isActive: false,
    mfaEnabled: true,
    lastLoginAt: '2026-07-02T16:20:00.000Z',
    createdAt: '2026-03-01T08:00:00.000Z',
  },
];

// ── Station users (FGA-85 / M18) ─────────────────────────────────────────────
function makeStationUser(
  stationId: string,
  body: Record<string, unknown>,
): Record<string, unknown> {
  const userType = (body['userType'] as string) ?? 'dispatcher';
  return {
    id: crypto.randomUUID(),
    stationId,
    userType,
    email: (body['email'] as string) ?? 'user@estacion.test',
    fullName: (body['fullName'] as string) ?? 'Usuario',
    phone: (body['phone'] as string) ?? null,
    locale: (body['locale'] as string) ?? 'es-GT',
    isActive: true,
    roleCodes: [userType],
    createdAt: now(),
  };
}

// Sembrado para STATION_ID con los mismos despachadores que OPEN_CONTEXT (FGA-28).
const STATION_USERS: Record<string, unknown>[] = [
  {
    id: DISPATCHER_1,
    stationId: STATION_ID,
    userType: 'dispatcher',
    email: 'pedro@estacion.test',
    fullName: 'Pedro Despachador',
    phone: null,
    locale: 'es-GT',
    isActive: true,
    roleCodes: ['dispatcher'],
    createdAt: now(),
  },
  {
    id: DISPATCHER_2,
    stationId: STATION_ID,
    userType: 'dispatcher',
    email: 'lucia@estacion.test',
    fullName: 'Lucía Despachadora',
    phone: null,
    locale: 'es-GT',
    isActive: true,
    roleCodes: ['dispatcher'],
    createdAt: now(),
  },
];

// ── Navegación server-driven (M21) ────────────────────────────────────────────
//
// Copia del seed `20260807130000_navigation_catalog` y de `ROLE_PERMISSIONS`
// (fga-api-core/libs/auth/src/permissions.ts). Se arma acá porque sin mock el
// portal en dev queda sin menú y sin rutas: el §4.1 del contrato prohíbe el
// fallback local, así que el mock ES el camino feliz de desarrollo.

const NAV_PERMISSIONS: Record<string, PermissionCode[]> = {
  SUPER_ADMIN: [
    'tracking.read',
    'fleet.vehicles.read',
    'fleet.vehicles.write',
    'fleet.drivers.read',
    'fleet.drivers.write',
    'fleet.devices.read',
    'fleet.devices.write',
    'stations.read',
    'stations.write',
    'station.users.read',
    'station.users.write',
    'transactions.read',
    'credit.topups.read',
    'credit.topups.write',
    'credit.rules.read',
    'credit.rules.write',
    'reports.read',
    'alerts.read',
    'alerts.write',
    'audit.read',
    'fiscal.read',
    'fiscal.write',
    'privacy.read',
    'privacy.write',
    'legal-documents.read',
    'legal-documents.write',
    'dispatch.read',
    'dispatch.write',
    'dispatch.shifts.read',
    'dispatch.shifts.write',
    'back-office.tenants.read',
    'back-office.tenants.write',
    'back-office.super-admins.read',
    'back-office.super-admins.write',
    'back-office.countries.read',
    'back-office.countries.write',
    'back-office.fuel-types.read',
    'back-office.fuel-types.write',
    'settings.read',
    'settings.write',
  ],
  ADMIN_FLEET: [
    'tracking.read',
    'fleet.vehicles.read',
    'fleet.vehicles.write',
    'fleet.drivers.read',
    'fleet.drivers.write',
    'fleet.devices.read',
    'fleet.devices.write',
    'geofences.read',
    'geofences.write',
    'transactions.read',
    'credit.balance.read',
    'credit.rules.read',
    'credit.rules.write',
    'reports.read',
    'alerts.read',
    'alerts.write',
    'audit.read',
    'fiscal.read',
    'fiscal.write',
    'privacy.read',
    'privacy.write',
    'bulk-import.read',
    'bulk-import.write',
    'settings.read',
    'settings.write',
  ],
  STANDARD_USER: [
    'tracking.read',
    'fleet.vehicles.read',
    'fleet.drivers.read',
    'transactions.read',
    'reports.read',
    'alerts.read',
    'settings.read',
  ],
  STATION_BASIC: [
    'dispatch.read',
    'dispatch.write',
    'dispatch.shifts.read',
    'dispatch.shifts.write',
    'station.users.read',
    'station.users.write',
    'transactions.read',
    'settings.read',
  ],
  DISPATCHER: ['dispatch.read', 'dispatch.write', 'dispatch.shifts.read', 'settings.read'],
  DRIVER: ['driver.self.read'],
};

interface NavSeedItem {
  section: string;
  key: string;
  label: string;
  icon: string | null;
  route: string | null;
  permission: PermissionCode;
  order: number;
  enabled: boolean;
  isNew?: boolean;
  parent?: string;
}

const NAV_SECTION_SEED = [
  {
    key: 'principal',
    label: 'Principal',
    order: 10,
    showHeader: false,
    collapsible: false,
    defaultCollapsed: false,
  },
  {
    key: 'gestion',
    label: 'Gestión',
    order: 20,
    showHeader: true,
    collapsible: true,
    defaultCollapsed: false,
  },
  {
    key: 'informacion',
    label: 'Información',
    order: 30,
    showHeader: true,
    collapsible: true,
    defaultCollapsed: false,
  },
  {
    key: 'configuracion',
    label: 'Configuración',
    order: 40,
    showHeader: true,
    collapsible: true,
    defaultCollapsed: true,
  },
];

const NAV_ITEM_SEED: NavSeedItem[] = [
  {
    section: 'principal',
    key: 'tracking',
    label: 'Tracking',
    icon: 'pi pi-map',
    route: '/app/tracking',
    permission: 'tracking.read',
    order: 10,
    enabled: true,
  },
  {
    section: 'principal',
    key: 'station-portal',
    label: 'Portal Estación',
    icon: 'pi pi-shop',
    route: '/app/station-portal',
    permission: 'dispatch.shifts.read',
    order: 20,
    enabled: true,
  },

  {
    section: 'gestion',
    key: 'fleet.vehicles',
    label: 'Flota',
    icon: 'pi pi-truck',
    route: '/app/fleet/vehicles',
    permission: 'fleet.vehicles.read',
    order: 10,
    enabled: true,
  },
  {
    section: 'gestion',
    key: 'fleet.drivers',
    label: 'Conductores',
    icon: 'pi pi-id-card',
    route: '/app/fleet/drivers',
    permission: 'fleet.drivers.read',
    order: 20,
    enabled: true,
  },
  {
    section: 'gestion',
    key: 'fleet.devices',
    label: 'Dispositivos GPS',
    icon: 'pi pi-microchip',
    route: '/app/fleet/devices',
    permission: 'fleet.devices.read',
    order: 30,
    enabled: true,
  },
  {
    section: 'gestion',
    key: 'geofences',
    label: 'Geocercas',
    icon: 'pi pi-globe',
    route: '/app/geofences',
    permission: 'geofences.read',
    order: 40,
    enabled: true,
  },
  {
    section: 'gestion',
    key: 'stations',
    label: 'Estaciones',
    icon: 'pi pi-building',
    route: '/app/stations',
    permission: 'stations.read',
    order: 50,
    enabled: true,
  },

  {
    section: 'informacion',
    key: 'transactions',
    label: 'Transacciones',
    icon: 'pi pi-list',
    route: '/app/transactions',
    permission: 'transactions.read',
    order: 10,
    enabled: false,
  },
  {
    section: 'informacion',
    key: 'credit.topups',
    label: 'Recargas',
    icon: 'pi pi-money-bill',
    route: '/app/back-office/credit-topups',
    permission: 'credit.topups.read',
    order: 20,
    enabled: true,
  },
  {
    section: 'informacion',
    key: 'reports',
    label: 'Reportes',
    icon: 'pi pi-chart-bar',
    route: '/app/reports',
    permission: 'reports.read',
    order: 30,
    enabled: false,
  },
  {
    section: 'informacion',
    key: 'alerts',
    label: 'Alertas',
    icon: 'pi pi-bell',
    route: '/app/alerts',
    permission: 'alerts.read',
    order: 40,
    enabled: false,
  },
  {
    section: 'informacion',
    key: 'audit',
    label: 'Bitácora',
    icon: 'pi pi-clock',
    route: '/app/audit',
    permission: 'audit.read',
    order: 50,
    enabled: true,
  },

  {
    section: 'configuracion',
    key: 'credit.rules',
    label: 'Reglas de Crédito',
    icon: 'pi pi-shield',
    route: '/app/credit-rules',
    permission: 'credit.rules.read',
    order: 10,
    enabled: true,
  },
  {
    section: 'configuracion',
    key: 'credit.balance',
    label: 'Saldo',
    icon: 'pi pi-wallet',
    route: '/app/credit-balance',
    permission: 'credit.balance.read',
    order: 20,
    enabled: true,
  },
  {
    section: 'configuracion',
    key: 'fiscal',
    label: 'Facturación',
    icon: 'pi pi-file',
    route: '/app/fiscal',
    permission: 'fiscal.read',
    order: 30,
    enabled: false,
  },
  {
    section: 'configuracion',
    key: 'privacy',
    label: 'Privacidad',
    icon: 'pi pi-lock',
    route: '/app/privacy',
    permission: 'privacy.read',
    order: 40,
    enabled: false,
  },
  // Sin pantalla en develop: es el caso de prueba de la validación contra router.config.
  {
    section: 'configuracion',
    key: 'legal-documents',
    label: 'Documentos Legales',
    icon: 'pi pi-file-check',
    route: '/app/legal-documents',
    permission: 'legal-documents.read',
    order: 50,
    enabled: true,
    isNew: true,
  },
  {
    section: 'configuracion',
    key: 'bulk-import',
    label: 'Carga Masiva',
    icon: 'pi pi-upload',
    route: '/app/bulk-import',
    permission: 'bulk-import.read',
    order: 60,
    enabled: false,
  },
  {
    section: 'configuracion',
    key: 'back-office',
    label: 'Back Office',
    icon: 'pi pi-cog',
    route: null,
    permission: 'back-office.tenants.read',
    order: 70,
    enabled: true,
  },
  {
    section: 'configuracion',
    key: 'settings',
    label: 'Ajustes',
    icon: 'pi pi-sliders-h',
    route: '/app/settings',
    permission: 'settings.read',
    order: 80,
    enabled: true,
  },

  {
    section: 'configuracion',
    parent: 'back-office',
    key: 'back-office.tenants',
    // Etiqueta tal como la sirve el backend en staging (el label viene traducido
    // del servidor; el mock lo copia para no mostrar en dev un menú distinto).
    label: 'Clientes',
    icon: 'pi pi-building',
    route: '/app/back-office/tenants',
    permission: 'back-office.tenants.read',
    order: 10,
    enabled: true,
  },
  {
    section: 'configuracion',
    parent: 'back-office',
    key: 'back-office.super-admins',
    label: 'Super admins',
    icon: 'pi pi-shield',
    // A diferencia del seed del backend (que sale COMING_SOON con route null hasta
    // que el portal se despliega), acá va habilitado y con ruta: en este build la
    // pantalla EXISTE, y el mock debe reflejar el estado post-flip para que el
    // menú de dev y de la demo llegue a ella.
    route: '/app/back-office/super-admins',
    permission: 'back-office.super-admins.read',
    order: 30,
    enabled: true,
  },
  {
    section: 'configuracion',
    parent: 'back-office',
    key: 'back-office.fuel-types',
    label: 'Tipos de Combustible',
    icon: 'pi pi-bolt',
    // Sin pantalla ni endpoint de administración del catálogo: igual que Países.
    // El backend lo marcó COMING_SOON al liberar GET /v1/fuel-types para los
    // roles de flota (migración 20260817120000_fuel_types_shared_catalog); el
    // mock lo copia para que el menú offline no prometa una ruta que el router
    // no registra.
    route: null,
    permission: 'back-office.fuel-types.read',
    order: 40,
    enabled: false,
  },
  {
    section: 'configuracion',
    parent: 'back-office',
    key: 'back-office.countries',
    label: 'Países',
    icon: 'pi pi-flag',
    route: null,
    permission: 'back-office.countries.read',
    order: 50,
    enabled: false,
  },
];

function navNode(seed: NavSeedItem): Record<string, unknown> {
  return {
    key: seed.key,
    label: seed.label,
    icon: seed.icon,
    route: seed.route,
    order: seed.order,
    isNew: seed.isNew ?? false,
    disabled: !seed.enabled,
    disabledReason: seed.enabled ? null : 'COMING_SOON',
    disabledMessage: seed.enabled ? null : 'Próximamente',
    collapsible: false,
    defaultCollapsed: false,
  };
}

/** Reproduce el filtrado por permisos que hace el backend: sección sin ítems no se emite. */
function mockNavigation(role: string): Record<string, unknown> {
  const granted = new Set(NAV_PERMISSIONS[role] ?? []);
  const visible = NAV_ITEM_SEED.filter((i) => granted.has(i.permission));

  const sections = NAV_SECTION_SEED.map((section) => {
    const items = visible
      .filter((i) => i.section === section.key && !i.parent)
      .sort((a, b) => a.order - b.order)
      .map((item) => {
        const children = visible
          .filter((c) => c.parent === item.key)
          .sort((a, b) => a.order - b.order)
          .map(navNode);
        const node = navNode(item);
        return {
          ...node,
          // El seed real hardcodea `false, false` para todo ítem, incluido
          // `back-office` (el único grupo anidado del producto) — replicarlo acá
          // evita que el mock arranque colapsado en dev y expandido en prod.
          collapsible: false,
          defaultCollapsed: false,
          children,
        };
      });
    return { ...section, icon: null, items };
  }).filter((s) => s.items.length > 0);

  return { locale: 'es', permissions: [...granted], sections };
}

// ── Interceptor ───────────────────────────────────────────────────────────────

type MockResult = { status: number; body: unknown } | null;

@Injectable()
export class MockInterceptor implements HttpInterceptor {
  /**
   * Último access token emitido por un flujo de auth. El mock corre ANTES que el
   * AuthInterceptor, así que no ve el `Authorization` en el request; guardamos acá
   * quién se autenticó para poder responder `GET /v1/auth/me` de forma coherente.
   */
  private lastAuthToken = ADMIN_JWT;

  /** Registra el token emitido (para `/auth/me`) y lo devuelve para armar el par. */
  private issue(token: string): string {
    this.lastAuthToken = token;
    return token;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Only intercept requests to our API base
    if (!req.url.startsWith(environment.apiUrl)) return next.handle(req);

    const path = req.url.replace(environment.apiUrl, '');
    const env = environment as typeof environment & { useMocks?: boolean };

    // Mocks activos solo con useMocks=true (development → backend local).
    // En mvp/staging/prod (useMocks=false) TODO el tráfico va al backend real,
    // incluido tracking (FGA-13/FGA-14 ya integran contra /v1/tracking).
    if (!env.useMocks) return next.handle(req);

    const result = this.match(
      req.method,
      path,
      req.body as Record<string, unknown> | null,
      req.headers.get('X-Act-As-Tenant-Id'),
    );
    if (result === null) return next.handle(req);

    if (result.status >= 400) {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: result.status,
            statusText: 'Error',
            error: result.body,
            url: req.url,
          }),
      ).pipe(delay(300)) as Observable<HttpEvent<unknown>>;
    }

    return of(
      new HttpResponse<unknown>({ body: result.body, status: result.status, statusText: 'OK' }),
    ).pipe(delay(300)) as Observable<HttpEvent<unknown>>;
  }

  private match(
    method: string,
    path: string,
    body: Record<string, unknown> | null,
    actAsTenantId: string | null = null,
  ): MockResult {
    // ── Health ────────────────────────────────────────────────────────────────
    if (path === '/health/live' || path === '/health/ready') return ok({ status: 'ok' });
    if (method === 'GET' && path === '/v1/health/rls') {
      return ok({
        tenantId: 'mock-tenant-001',
        isSuperAdmin: false,
        rowCount: 8,
        checkedAt: now(),
      });
    }

    // ── Auth ──────────────────────────────────────────────────────────────────
    if (method === 'POST' && path === '/v1/auth/login') {
      const email = String(body?.['email'] ?? '');
      // FGA-90: gate de cambio de contraseña (PRECEDE a MFA). Demo: cualquier email
      // que contenga "reset" o "mustchange" simula must_change_password=true.
      if (email.includes('reset') || email.includes('mustchange')) {
        return ok({
          password_change_required: true,
          change_token: `mock-change-${Date.now()}`,
          password_policy: MOCK_PASSWORD_POLICY,
        });
      }
      // superadmin → token directo (el mfaGuard está bypasseado en mock mode)
      if (email.includes('super')) return ok(pair(this.issue(SUPER_JWT)));
      if (email.includes('station')) return ok(pair(this.issue(STATION_JWT)));
      if (email.includes('user')) return ok(pair(this.issue(USER_JWT)));
      return ok(pair(this.issue(ADMIN_JWT)));
    }
    if (method === 'GET' && path === '/v1/auth/me') return ok(mockMe(this.lastAuthToken));
    // FGA-160 — tenant EFECTIVO. Honra `X-Act-As-Tenant-Id` a propósito: si el mock
    // devolviera siempre el tenant del actor, el bug que este ticket arregla no se
    // podría reproducir ni verificar en dev.
    if (method === 'GET' && path === '/v1/me/context') {
      const claims = decodeMockClaims(this.lastAuthToken);
      const ownTenantId = (claims['tenant_id'] as string | undefined) ?? null;
      const effectiveId = actAsTenantId ?? ownTenantId;
      return ok({
        actingAs: actAsTenantId !== null,
        tenant: effectiveId
          ? {
              id: effectiveId,
              countryCode: String(claims['country_code'] ?? 'GT'),
              currencyCode: String(claims['country_code'] ?? 'GT') === 'CR' ? 'CRC' : 'GTQ',
              locale: String(claims['locale'] ?? 'es-GT'),
              timezone: String(claims['timezone'] ?? 'America/Guatemala'),
            }
          : null,
      });
    }
    // M21 — navegación server-driven. El rol sale del JWT emitido en el login.
    if (method === 'GET' && path === '/v1/me/navigation') {
      const role = String(decodeMockClaims(this.lastAuthToken)['role'] ?? '');
      return ok(mockNavigation(role));
    }
    if (method === 'POST' && path === '/v1/auth/refresh') return ok(pair(this.issue(ADMIN_JWT)));
    if (method === 'POST' && path === '/v1/auth/logout') return ok({ success: true });
    if (method === 'POST' && path === '/v1/auth/mfa/challenge')
      return ok({ method_type: 'totp', masked_hint: null });
    if (method === 'POST' && path === '/v1/auth/mfa/verify') return ok(pair(this.issue(SUPER_JWT)));
    if (method === 'POST' && path === '/v1/auth/mfa/enroll') return ok(MFA_ENROLL);
    if (method === 'POST' && path === '/v1/auth/mfa/enroll/confirm')
      return ok({ activated: true, verified_at: now() });
    if (method === 'POST' && path === '/v1/auth/mfa/setup/enroll') return ok(MFA_ENROLL);
    if (method === 'POST' && path === '/v1/auth/mfa/setup/enroll/confirm')
      return ok({ activated: true, verified_at: now() });
    if (method === 'POST' && path === '/v1/auth/password-setup') return ok({ success: true });
    // FGA-90: cambio forzado. Replica la política real del backend
    // (PasswordPolicyService): `violation` es un ENUM, no un mensaje. El mock
    // devolvía una frase en español y por eso el portal pintaba el código crudo
    // en staging sin que dev lo notara — no lo "humanices" de nuevo aquí.
    if (method === 'POST' && path === '/v1/auth/change-password') {
      const newPass = String(body?.['new_password'] ?? '');
      const violation = mockPolicyViolation(newPass);
      if (violation) {
        return { status: 400, body: { code: 'WEAK_PASSWORD', violation } };
      }
      return ok({ success: true });
    }
    if (method === 'POST' && /^\/v1\/auth\/sso\/[^/]+\/initiate$/.test(path)) {
      return ok({
        nonce: 'mock-nonce',
        state: 'mock-state',
        authorization_url: 'https://accounts.google.com/mock',
      });
    }
    if (method === 'POST' && /^\/v1\/auth\/sso\/[^/]+\/callback$/.test(path))
      return ok(pair(ADMIN_JWT));

    // ── Audit ─────────────────────────────────────────────────────────────────
    if (method === 'GET' && path.startsWith('/v1/audit/logs') && !path.match(/\/logs\/[^/]+$/)) {
      return ok({ items: AUDIT_LOGS, page: 1, pageSize: 50, total: AUDIT_LOGS.length });
    }
    if (method === 'GET' && /^\/v1\/audit\/logs\/[^/]+$/.test(path)) {
      return ok({
        ...AUDIT_LOGS[0],
        beforeData: {
          current_odometer: 145200,
          updated_at: '2026-06-07T10:00:00Z',
          plate: 'P-123ABC',
        },
        afterData: {
          current_odometer: 145850,
          updated_at: '2026-06-08T10:00:00Z',
          plate: 'P-123ABC',
        },
        note: null,
      });
    }
    if (method === 'GET' && path.startsWith('/v1/audit/cross-tenant')) {
      return ok({ items: [], page: 1, pageSize: 50, total: 0 });
    }
    if (method === 'POST' && path === '/v1/audit/exports') {
      // ⚠️ 202 devuelve AuditExportEnqueueResult (NO el job completo)
      return {
        status: 202,
        body: {
          jobId: 'mock-job-001',
          status: 'queued',
          estimatedRows: AUDIT_LOGS.length,
        },
      };
    }
    if (method === 'GET' && /^\/v1\/audit\/exports\/[^/]+$/.test(path)) {
      return ok({
        jobId: 'mock-job-001',
        status: 'completed',
        fileId: 'mock-file-export',
        downloadUrl: '#mock-download',
        expiresAt: new Date(Date.now() + 86_400_000).toISOString(),
        rowCount: AUDIT_LOGS.length,
        format: 'csv',
        error: null,
      });
    }

    // ── Shifts / turnos de estación (FGA-28 / M16) ────────────────────────────
    if (method === 'GET' && path.startsWith('/v1/shifts/open-context')) {
      const open = [...SHIFT_STORE.values()].find((s) => s['status'] === 'open');
      return ok({ ...OPEN_CONTEXT, openShiftId: open ? (open['id'] as string) : null });
    }
    if (method === 'POST' && path === '/v1/shifts') {
      const hasOpen = [...SHIFT_STORE.values()].some((s) => s['status'] === 'open');
      if (hasOpen) {
        return {
          status: 409,
          body: { code: 'SHIFT_ALREADY_OPEN', message: 'Ya hay un turno abierto en la estación.' },
        };
      }
      const priceError = validateFuelPrices(body ?? {});
      if (priceError) return priceError;
      return { status: 201, body: makeOpenShift(body ?? {}) };
    }
    const shiftClosePath = path.match(/^\/v1\/shifts\/([^/?]+)\/close$/);
    if (method === 'POST' && shiftClosePath) {
      const detail = SHIFT_STORE.get(shiftClosePath[1]);
      if (!detail) return { status: 404, body: { code: 'SHIFT_NOT_FOUND' } };
      if (detail['status'] !== 'open') return { status: 409, body: { code: 'SHIFT_NOT_OPEN' } };
      return ok(closeShift(detail, body ?? {}));
    }
    // FGA-30 — ANTES del match genérico de :id y de la lista.
    const shiftTxPath = path.match(/^\/v1\/shifts\/([^/?]+)\/transactions(?:\?.*)?$/);
    if (method === 'GET' && shiftTxPath) {
      const detail = SHIFT_STORE.get(shiftTxPath[1]);
      if (!detail) return { status: 404, body: { code: 'SHIFT_NOT_FOUND' } };
      if (detail['status'] !== 'open') return { status: 409, body: { code: 'SHIFT_NOT_OPEN' } };
      return ok(makeShiftTransactions());
    }
    const shiftIdPath = path.match(/^\/v1\/shifts\/([^/?]+)$/);
    if (method === 'GET' && shiftIdPath) {
      const detail = SHIFT_STORE.get(shiftIdPath[1]);
      return detail ? ok(detail) : { status: 404, body: { code: 'SHIFT_NOT_FOUND' } };
    }
    if (method === 'GET' && path.startsWith('/v1/shifts')) {
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      let items = [...SHIFT_STORE.values()];
      const status = qs.get('status');
      if (status) items = items.filter((s) => s['status'] === status);
      return ok({ items: items.map(shiftListItem), nextCursor: null });
    }

    // ── Station users (FGA-85 / M18) ──────────────────────────────────────────
    const stationUsersPath = path.match(/^\/v1\/stations\/([^/?]+)\/users(?:\?.*)?$/);
    if (method === 'GET' && stationUsersPath) {
      const sid = stationUsersPath[1];
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      let items = STATION_USERS.filter((u) => u['stationId'] === sid);
      const userType = qs.get('userType');
      if (userType) items = items.filter((u) => u['userType'] === userType);
      const isActive = qs.get('isActive');
      if (isActive !== null) items = items.filter((u) => String(u['isActive']) === isActive);
      return ok({ items, nextCursor: null });
    }
    if (method === 'POST' && stationUsersPath) {
      const sid = stationUsersPath[1];
      const email = String(body?.['email'] ?? '');
      if (STATION_USERS.some((u) => u['stationId'] === sid && u['email'] === email)) {
        return { status: 409, body: { code: 'EMAIL_ALREADY_EXISTS' } };
      }
      const user = makeStationUser(sid, body ?? {});
      STATION_USERS.push(user);
      return { status: 201, body: user };
    }
    // Reset de contraseña de usuario de estación (HU-M18-007) — station-scoped, sin act-as.
    const stationUserResetPath = path.match(
      /^\/v1\/stations\/([^/?]+)\/users\/([^/?]+)\/reset-password$/,
    );
    if (method === 'POST' && stationUserResetPath) {
      const user = STATION_USERS.find((u) => u['id'] === stationUserResetPath[2]);
      if (!user) return { status: 404, body: { code: 'USER_NOT_FOUND' } };
      return ok({
        user_id: user['id'],
        temp_password: genTempPassword(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        must_change_password: true,
      });
    }
    const stationUserPath = path.match(/^\/v1\/stations\/([^/?]+)\/users\/([^/?]+)$/);
    if (method === 'PATCH' && stationUserPath) {
      const user = STATION_USERS.find((u) => u['id'] === stationUserPath[2]);
      if (!user) return { status: 404, body: { code: 'USER_NOT_FOUND' } };
      user['isActive'] = Boolean(body?.['isActive']);
      return ok(user);
    }
    if (method === 'DELETE' && stationUserPath) {
      const idx = STATION_USERS.findIndex((u) => u['id'] === stationUserPath[2]);
      if (idx < 0) return { status: 404, body: { code: 'USER_NOT_FOUND' } };
      STATION_USERS.splice(idx, 1);
      return { status: 204, body: null };
    }

    // ── Super admins (back-office) ────────────────────────────────────────────
    // Replica las reglas de protección del backend para que la pantalla pueda
    // ejercitar sus mensajes de error en dev, no sólo el camino feliz.
    const superAdminResetPath = path.match(/^\/v1\/super-admins\/([^/?]+)\/reset-password$/);
    if (method === 'POST' && superAdminResetPath) {
      const target = SUPER_ADMINS.find((u) => u['id'] === superAdminResetPath[1]);
      if (!target) return { status: 404, body: { code: 'TARGET_NOT_FOUND' } };
      if (target['id'] === SUPER_ADMIN_SELF_ID)
        return { status: 422, body: { code: 'TARGET_IS_SELF' } };
      if (!target['isActive']) return { status: 422, body: { code: 'TARGET_INACTIVE' } };
      const justification = String(body?.['justification'] ?? '');
      if (justification.length < 5 || justification.length > 500) {
        return { status: 400, body: { code: 'VALIDATION_ERROR' } };
      }
      return ok({
        user_id: target['id'],
        temp_password: genTempPassword(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        must_change_password: true,
      });
    }
    const superAdminStatusPath = path.match(/^\/v1\/super-admins\/([^/?]+)\/status$/);
    if (method === 'PATCH' && superAdminStatusPath) {
      const target = SUPER_ADMINS.find((u) => u['id'] === superAdminStatusPath[1]);
      if (!target) return { status: 404, body: { code: 'TARGET_NOT_FOUND' } };
      if (target['id'] === SUPER_ADMIN_SELF_ID)
        return { status: 422, body: { code: 'TARGET_IS_SELF' } };
      const next = Boolean(body?.['isActive']);
      const activos = SUPER_ADMINS.filter((u) => u['isActive']).length;
      if (!next && target['isActive'] && activos <= 1) {
        return { status: 409, body: { code: 'LAST_ACTIVE_SUPER_ADMIN' } };
      }
      target['isActive'] = next;
      return ok(target);
    }
    const superAdminIdPath = path.match(/^\/v1\/super-admins\/([^/?]+)$/);
    // Edición de perfil: sólo el nombre. Sin reglas de auto-protección — la fila
    // propia y los inactivos también se editan (a diferencia de /status).
    if (method === 'PATCH' && superAdminIdPath) {
      const target = SUPER_ADMINS.find((u) => u['id'] === superAdminIdPath[1]);
      if (!target) return { status: 404, body: { code: 'TARGET_NOT_FOUND' } };
      const fullName = String(body?.['fullName'] ?? '').trim();
      if (!fullName || fullName.length > 160) {
        return { status: 400, body: { code: 'VALIDATION_ERROR' } };
      }
      target['fullName'] = fullName;
      return ok(target);
    }
    if (method === 'DELETE' && superAdminIdPath) {
      const idx = SUPER_ADMINS.findIndex((u) => u['id'] === superAdminIdPath[1]);
      if (idx < 0) return { status: 404, body: { code: 'TARGET_NOT_FOUND' } };
      const target = SUPER_ADMINS[idx];
      // Precedencia del contrato: SELF → LAST_ACTIVE → ACCOUNT_MANAGER.
      if (target['id'] === SUPER_ADMIN_SELF_ID)
        return { status: 422, body: { code: 'TARGET_IS_SELF' } };
      const activos = SUPER_ADMINS.filter((u) => u['isActive']).length;
      if (target['isActive'] && activos <= 1) {
        return { status: 409, body: { code: 'LAST_ACTIVE_SUPER_ADMIN' } };
      }
      if (target['id'] === SUPER_ADMIN_MANAGER_ID) {
        return {
          status: 409,
          body: {
            code: 'TARGET_IS_ACCOUNT_MANAGER',
            details: {
              tenants: [
                {
                  id: 'mock-tenant-001',
                  commercialName: 'Acme Logística (mock)',
                  subdomain: 'acme',
                },
                { id: 'mock-tenant-002', commercialName: 'Transportes Beta', subdomain: 'beta' },
              ],
              total: 2,
            },
          },
        };
      }
      SUPER_ADMINS.splice(idx, 1);
      return { status: 204, body: null };
    }
    if (method === 'POST' && path === '/v1/super-admins') {
      const email = String(body?.['email'] ?? '');
      // La unicidad del email es GLOBAL: también choca con usuarios de estación.
      const duplicado =
        SUPER_ADMINS.some((u) => u['email'] === email) ||
        STATION_USERS.some((u) => u['email'] === email);
      if (duplicado) return { status: 409, body: { code: 'EMAIL_ALREADY_EXISTS' } };
      const created: Record<string, unknown> = {
        id: crypto.randomUUID(),
        email,
        fullName: String(body?.['fullName'] ?? ''),
        phone: body?.['phone'] ?? null,
        // Sin entidad padre de la que heredar: queda null si no se manda.
        locale: body?.['locale'] ?? null,
        isActive: true,
        mfaEnabled: false,
        lastLoginAt: null,
        createdAt: now(),
      };
      SUPER_ADMINS.push(created);
      return {
        status: 201,
        body: {
          ...created,
          credentials: {
            temp_password: genTempPassword(),
            expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
            must_change_password: true,
          },
        },
      };
    }
    if (method === 'GET' && path.startsWith('/v1/super-admins')) {
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      let items = [...SUPER_ADMINS];
      const q = qs.get('q');
      if (q) {
        const needle = q.toLowerCase();
        items = items.filter(
          (u) =>
            String(u['email']).toLowerCase().includes(needle) ||
            String(u['fullName']).toLowerCase().includes(needle),
        );
      }
      const isActive = qs.get('isActive');
      if (isActive !== null) items = items.filter((u) => String(u['isActive']) === isActive);
      return ok({ items, nextCursor: null });
    }

    // ── Stations ──────────────────────────────────────────────────────────────
    if (
      method === 'GET' &&
      path.startsWith('/v1/stations') &&
      !path.replace('/v1/stations', '').match(/^\/[^/?]/)
    )
      return ok({ items: STATIONS, nextCursor: null });
    if (method === 'POST' && path === '/v1/stations') {
      return {
        status: 201,
        body: {
          ...STATIONS[0],
          id: `sta-${Date.now()}`,
          code: String(body?.['code'] ?? 'GAS-NEW'),
          name: String(body?.['name'] ?? 'Nueva Estación'),
        },
      };
    }
    const staId = !path.includes('/places/')
      ? path.match(/^\/v1\/stations\/([^/?]+)(\?.*)?$/)
      : null;
    if (method === 'GET' && staId) return ok(stationDetail(staId[1]));
    if (method === 'PATCH' && staId) return ok({ ...stationDetail(staId[1]), ...body });
    // ⚠️ upload multipart (campo `file`) — no hay `area` en el body que leer
    if (method === 'POST' && /^\/v1\/stations\/[^/]+\/polygon$/.test(path))
      return ok({
        id: 'sta001',
        polygon: {
          type: 'Polygon',
          coordinates: [
            [
              [-90.5181, 14.6042],
              [-90.5178, 14.6042],
              [-90.5178, 14.6045],
              [-90.5181, 14.6045],
              [-90.5181, 14.6042],
            ],
          ],
        },
        centroid: { lat: 14.6042, lng: -90.5181 },
        boundingBox: { minLng: -90.5181, minLat: 14.6042, maxLng: -90.5178, maxLat: 14.6045 },
        updatedAt: now(),
      });
    if (method === 'POST' && /^\/v1\/stations\/[^/]+\/islands(\?.*)?$/.test(path))
      return {
        status: 201,
        body: { islandId: `isl-${Date.now()}`, pumpIds: [] },
      };
    if (method === 'POST' && /^\/v1\/stations\/[^/]+\/islands\/[^/]+\/pumps(\?.*)?$/.test(path)) {
      const islandId = path.match(/\/islands\/([^/]+)\/pumps/)?.[1] ?? '';
      return {
        status: 201,
        body: {
          id: `pmp-${Date.now()}`,
          islandId,
          code: body?.['code'],
          controllerId: body?.['controllerId'] ?? null,
          nfcTagId: body?.['nfcTagId'] ?? null,
          status: 'active',
          fuelTypeCodes: (body?.['fuelTypeCodes'] as string[]) ?? [],
        },
      };
    }
    // ⚠️ PATCH/PUT pump paths do NOT include /islands/:islandId/
    if (method === 'PATCH' && /^\/v1\/stations\/[^/]+\/pumps\/[^/]+(\?.*)?$/.test(path))
      return ok({ ...body });
    if (method === 'PUT' && /^\/v1\/stations\/[^/]+\/pumps\/[^/]+\/fuel-types(\?.*)?$/.test(path))
      return ok([]);
    if (method === 'PUT' && /^\/v1\/stations\/[^/]+\/schedules\/\d+(\?.*)?$/.test(path))
      return ok(body);
    // ⚠️ placesAutocomplete is GET with query params
    if (method === 'GET' && path.startsWith('/v1/stations/places/autocomplete')) {
      return ok({
        predictions: [
          {
            placeId: 'mock-place-1',
            description: 'Gasolinera Centro, Zona 9, Guatemala',
            mainText: 'Gasolinera Centro',
            secondaryText: 'Zona 9, Guatemala',
          },
        ],
        sessionToken: 'mock-session',
      });
    }
    // ⚠️ placeDetails is GET with ?placeId= query param (not path param)
    if (method === 'GET' && path.startsWith('/v1/stations/places/details')) {
      return ok({
        placeId: 'mock-place-1',
        formattedAddress: 'Avenida Reforma 12-45, Guatemala 01009',
        location: { lat: 14.60435, lng: -90.51795 },
        addressComponents: [],
      });
    }

    // ── Vehicles ──────────────────────────────────────────────────────────────
    if (method === 'POST' && path === '/v1/vehicles/decode-vin') {
      const vin = String(body?.['vin'] ?? '');
      return ok({
        vin,
        brand: 'Nissan',
        model: 'Versa',
        year: 2015,
        fuelType: 'Gasoline',
        vehicleClass: 'Passenger Car',
        source: 'nhtsa',
      });
    }
    if (method === 'POST' && path === '/v1/vehicles/ocr-plate') {
      return ok({ accepted: true, plate: 'P-123ABC', confidence: 0.94, alternatives: [] });
    }
    if (method === 'GET' && (path === '/v1/vehicles' || path.startsWith('/v1/vehicles?')))
      return ok({ items: VEHICLES, nextCursor: null });
    if (method === 'POST' && path === '/v1/vehicles') {
      return {
        status: 201,
        body: {
          id: `v-${Date.now()}`,
          plate: body?.['plate'],
          vin: body?.['vin'] ?? null,
          brand: body?.['brand'] ?? null,
          model: body?.['model'] ?? null,
          year: body?.['year'] ?? null,
          vehicleClass: body?.['vehicleClass'],
          fuelTypeCode: body?.['fuelTypeCode'],
          status: 'active',
          countryCode: body?.['countryCode'],
          createdAt: now(),
          updatedAt: now(),
        },
      };
    }

    const vId = path.match(/^\/v1\/vehicles\/([^/]+)$/);
    if (method === 'GET' && vId) return ok(vehicleDetail(vId[1]));
    if (method === 'PATCH' && vId) return ok({ ...vehicleDetail(vId[1]), ...body });

    const vPosSource = path.match(/^\/v1\/vehicles\/([^/]+)\/position-source$/);
    if (vPosSource) {
      const id = vPosSource[1];
      if (method === 'GET') return ok(positionSourceConfig(id));
      if (method === 'PATCH') {
        const next = String(body?.['positionSource'] ?? '');
        const state = POSITION_SOURCE_STATE[id];
        if ((next === 'gps_device' || next === 'both') && !state?.gpsDeviceId) {
          return {
            status: 422,
            body: {
              code: 'GPS_DEVICE_NOT_LINKED',
              message: 'El vehículo no tiene un dispositivo GPS vinculado.',
            },
          };
        }
        if (state) state.positionSource = next;
        return ok(positionSourceConfig(id));
      }
    }

    const vOdo = path.match(/^\/v1\/vehicles\/([^/]+)\/odometer$/);
    if (method === 'PATCH' && vOdo) {
      return ok({
        id: vOdo[1],
        currentOdometer: body?.['odometer'] ?? 145850,
        updatedAt: now(),
      });
    }

    const vStatus = path.match(/^\/v1\/vehicles\/([^/]+)\/status$/);
    if (method === 'PATCH' && vStatus) {
      return ok({ ...vehicleDetail(vStatus[1]), status: body?.['status'] ?? 'active' });
    }

    const vDocs = path.match(/^\/v1\/vehicles\/([^/]+)\/documents$/);
    if (method === 'GET' && vDocs) {
      return ok({
        items: DOCS.filter((d) => d.vehicleId === vDocs[1]).concat(vDocs[1] !== 'v001' ? [] : []),
      });
    }
    if (method === 'POST' && vDocs) {
      return {
        status: 201,
        body: {
          id: `doc-${Date.now()}`,
          vehicleId: vDocs[1],
          documentType: body?.['documentType'] ?? 'other',
          documentNumber: body?.['documentNumber'] ?? null,
          fileId: body?.['fileId'] ?? null,
          issuedAt: body?.['issuedAt'] ?? null,
          expiresAt: body?.['expiresAt'],
          issuingEntity: body?.['issuingEntity'] ?? null,
          notes: body?.['notes'] ?? null,
          isCurrent: true,
          createdAt: now(),
          updatedAt: now(),
        },
      };
    }

    if (method === 'DELETE' && /^\/v1\/vehicles\/[^/]+\/documents\/[^/]+$/.test(path)) {
      return { status: 204, body: null };
    }

    // ── Drivers ───────────────────────────────────────────────────────────────
    if (method === 'GET' && path === '/v1/drivers') return ok({ items: DRIVERS, nextCursor: null });

    if (method === 'POST' && path === '/v1/drivers') {
      return {
        status: 201,
        body: {
          id: `drv-${Date.now()}`,
          nationalId: body?.['nationalId'] ?? '',
          igssNumber: body?.['igssNumber'] ?? null,
          fullName: body?.['fullName'] ?? '',
          phone: body?.['phone'] ?? null,
          email: body?.['email'] ?? null,
          photoFileId: null,
          birthdate: body?.['birthdate'] ?? null,
          emergencyContactName: body?.['emergencyContactName'] ?? null,
          emergencyContactPhone: body?.['emergencyContactPhone'] ?? null,
          usualWorkStart: body?.['usualWorkStart'] ?? null,
          usualWorkEnd: body?.['usualWorkEnd'] ?? null,
          workDaysOfWeek: body?.['workDaysOfWeek'] ?? [],
          nfcTagId: body?.['nfcTagId'] ?? null,
          qrTagValue: `eyJkcml2ZXJJZCI6Im5ldyJ9.mock-qr-${Date.now()}`,
          status: 'active',
          createdAt: now(),
          updatedAt: now(),
        },
      };
    }

    const drvId = path.match(/^\/v1\/drivers\/([^/]+)$/);
    if (method === 'GET' && drvId) {
      return ok(DRIVERS.find((d) => d.id === drvId[1]) ?? DRIVERS[0]);
    }
    if (method === 'PATCH' && drvId) {
      const base = DRIVERS.find((d) => d.id === drvId[1]) ?? DRIVERS[0];
      return ok({ ...base, ...body, updatedAt: now() });
    }

    const drvPhoto = path.match(/^\/v1\/drivers\/([^/]+)\/photo$/);
    if (method === 'POST' && drvPhoto) {
      // Paso 2 del flujo de foto: recibe JSON `{ fileId }` (NO multipart) y corre el face-detect.
      return ok({
        driverId: drvPhoto[1],
        fileId: String(body?.['fileId'] ?? `photo-${Date.now()}`),
        faces: 1,
        confidence: 0.92,
        yaw: 2.1,
        pitch: -1.3,
      });
    }

    const drvLicense = path.match(/^\/v1\/drivers\/([^/]+)\/licenses$/);
    if (method === 'GET' && drvLicense) {
      return ok(DRIVER_LICENSES[drvLicense[1]] ?? []);
    }
    if (method === 'POST' && drvLicense) {
      return {
        status: 201,
        body: {
          id: `lic-${Date.now()}`,
          driverId: drvLicense[1],
          licenseNumber: body?.['licenseNumber'] ?? '',
          licenseType: body?.['licenseType'] ?? 'B',
          issuedAt: body?.['issuedAt'] ?? null,
          expiresAt: body?.['expiresAt'] ?? '',
          issuingCountry: body?.['issuingCountry'] ?? null,
          fileId: body?.['fileId'] ?? null,
          isCurrent: true,
          createdAt: now(),
          updatedAt: now(),
        },
      };
    }

    const drvAsgn = path.match(/^\/v1\/drivers\/([^/]+)\/assignments$/);
    if (method === 'GET' && drvAsgn) {
      // Array pelado (FGA-12 §9), NO envelope — el mock debe reflejar el contrato real.
      return ok(DRIVER_ASSIGNMENTS[drvAsgn[1]] ?? []);
    }
    if (method === 'POST' && drvAsgn) {
      const vId = String(body?.['vehicleId'] ?? '');
      const vehicle = VEHICLES.find((v) => v.id === vId) ?? VEHICLES[0];
      return {
        status: 201,
        body: {
          id: `asgn-${Date.now()}`,
          driverId: drvAsgn[1],
          vehicleId: vehicle.id,
          assignedFrom: now(),
          assignedTo: null,
          notes: (body?.['notes'] as string | undefined) ?? null,
          createdAt: now(),
          updatedAt: now(),
        },
      };
    }

    if (method === 'DELETE' && /^\/v1\/drivers\/[^/]+\/assignments\/[^/]+$/.test(path)) {
      return { status: 204, body: null };
    }

    // ── Tracking ──────────────────────────────────────────────────────────────
    if (method === 'GET' && path.startsWith('/v1/tracking/snapshot')) {
      return ok({
        generatedAt: new Date().toISOString(),
        truncated: false,
        items: [
          {
            vehicleId: '11111111-1111-1111-1111-111111111111',
            plate: 'P-123ABC',
            gpsDeviceId: 'aaaa1111-1111-1111-1111-111111111111',
            captureSource: 'device',
            lat: 14.629,
            lng: -90.514,
            heading: 270,
            speedKmh: 52,
            ignitionState: 'on',
            status: 'moving',
            lastPositionAt: new Date().toISOString(),
            isStale: false,
          },
          {
            // Vehículo sin device: la posición viene del celular del conductor.
            vehicleId: '22222222-2222-2222-2222-222222222222',
            plate: 'M-456DEF',
            gpsDeviceId: null,
            captureSource: 'mobile',
            lat: 14.618,
            lng: -90.527,
            heading: null,
            speedKmh: 0,
            ignitionState: 'off',
            status: 'stopped',
            lastPositionAt: new Date(Date.now() - 8 * 60_000).toISOString(),
            isStale: false,
          },
          {
            vehicleId: '33333333-3333-3333-3333-333333333333',
            plate: '1234-XYZ',
            gpsDeviceId: 'aaaa3333-3333-3333-3333-333333333333',
            lat: 14.641,
            lng: -90.501,
            heading: 90,
            speedKmh: 0,
            ignitionState: 'unknown',
            status: 'no_report',
            lastPositionAt: new Date(Date.now() - 30 * 60 * 60_000).toISOString(),
            isStale: true,
          },
          {
            vehicleId: '44444444-4444-4444-4444-444444444444',
            plate: 'C-789GHI',
            gpsDeviceId: 'aaaa4444-4444-4444-4444-444444444444',
            lat: null,
            lng: null,
            heading: null,
            speedKmh: null,
            ignitionState: null,
            status: 'no_report',
            lastPositionAt: null,
            isStale: false,
          },
        ],
      });
    }

    // ── Tracking history ──────────────────────────────────────────────────────
    if (method === 'GET' && path.startsWith('/v1/tracking/history')) {
      const qs = path.includes('?') ? path.slice(path.indexOf('?')) : '';
      const params = new URLSearchParams(qs);
      const format = params.get('format');
      if (format === 'kml' || format === 'gpx') {
        // Blob vacío para las descargas
        return ok(
          new Blob([], {
            type: format === 'kml' ? 'application/vnd.google-earth.kml+xml' : 'application/gpx+xml',
          }),
        );
      }
      return ok({
        vehicleId: '11111111-1111-1111-1111-111111111111',
        points: [
          {
            lat: 14.629,
            lng: -90.514,
            heading: 270,
            speedKmh: 52,
            ignitionState: 'on',
            occurredAt: '2026-06-22T10:00:00.000Z',
            classification: 'moving',
          },
          {
            lat: 14.632,
            lng: -90.52,
            heading: 270,
            speedKmh: 48,
            ignitionState: 'on',
            occurredAt: '2026-06-22T10:02:00.000Z',
            classification: 'moving',
            captureSource: 'device',
          },
          {
            lat: 14.635,
            lng: -90.525,
            heading: null,
            speedKmh: 0,
            ignitionState: 'off',
            occurredAt: '2026-06-22T10:15:00.000Z',
            classification: 'stop',
          },
          {
            lat: 14.635,
            lng: -90.525,
            heading: null,
            speedKmh: 0,
            ignitionState: 'on',
            occurredAt: '2026-06-22T10:25:00.000Z',
            classification: 'idle',
            // A partir de aquí el device dejó de reportar y siguió el celular.
            captureSource: 'mobile',
          },
          {
            lat: 14.64,
            lng: -90.53,
            heading: 90,
            speedKmh: 35,
            ignitionState: 'on',
            occurredAt: '2026-06-22T10:35:00.000Z',
            classification: 'moving',
            captureSource: 'mobile',
          },
        ],
        stops: [
          {
            type: 'stop',
            startAt: '2026-06-22T10:15:00.000Z',
            endAt: '2026-06-22T10:20:00.000Z',
            durationSeconds: 300,
            lat: 14.635,
            lng: -90.525,
          },
          {
            type: 'idle',
            startAt: '2026-06-22T10:20:00.000Z',
            endAt: '2026-06-22T10:35:00.000Z',
            durationSeconds: 900,
            lat: 14.635,
            lng: -90.525,
          },
        ],
        generatedAt: '2026-06-22T11:00:00.000Z',
        downsampled: false,
        totalRaw: 5,
        returned: 5,
      });
    }

    // ── Tenants ───────────────────────────────────────────────────────────────
    if (method === 'GET' && path.startsWith('/v1/tenants') && !path.match(/\/tenants\/[^/]+/)) {
      const qs = path.includes('?') ? path.slice(path.indexOf('?')) : '';
      const params = new URLSearchParams(qs);
      const status = params.get('status');
      const countryCode = params.get('countryCode');
      const search = params.get('search')?.toLowerCase();

      let items = [...MOCK_TENANTS_DB];
      if (status) items = items.filter((t) => t['status'] === status);
      if (countryCode) items = items.filter((t) => t['countryCode'] === countryCode);
      if (search) {
        items = items.filter(
          (t) =>
            String(t['commercialName']).toLowerCase().includes(search) ||
            String(t['legalName']).toLowerCase().includes(search),
        );
      }
      return ok({ items, nextCursor: null });
    }

    const tenId = path.match(/^\/v1\/tenants\/([^/]+)$/);
    if (method === 'GET' && tenId) {
      const tenant = MOCK_TENANTS_DB.find((t) => t['id'] === tenId[1]);
      return tenant ? ok(tenant) : { status: 404, body: { code: 'TENANT_NOT_FOUND' } };
    }

    if (method === 'POST' && path === '/v1/tenants') {
      const b = body as Record<string, unknown>;
      const countryCode = String(b?.['countryCode'] ?? 'GT');
      const derived =
        countryCode === 'CR'
          ? { currencyCode: 'CRC', timezone: 'America/Costa_Rica', locale: 'es-CR' }
          : { currencyCode: 'GTQ', timezone: 'America/Guatemala', locale: 'es-GT' };
      const newTenant = {
        id: `ten-${Date.now()}`,
        legalName: String(b?.['legalName'] ?? ''),
        commercialName: String(b?.['commercialName'] ?? ''),
        taxId: String(b?.['taxId'] ?? ''),
        countryCode,
        ...derived,
        subdomain: String(b?.['subdomain'] ?? ''),
        planCode: String(b?.['planCode'] ?? 'pilot'),
        status: 'provisioning',
        fugaAccountManagerId: null,
        notes: b?.['notes'] ? String(b['notes']) : null,
        onboardedAt: null,
        suspendedAt: null,
        suspendedReason: null,
        terminatedAt: null,
        createdAt: now(),
        updatedAt: now(),
      };
      MOCK_TENANTS_DB.push(newTenant);
      scheduleMockActivation(newTenant.id);
      return { status: 201, body: newTenant };
    }

    if (method === 'PATCH' && tenId) {
      const tenant = MOCK_TENANTS_DB.find((t) => t['id'] === tenId[1]);
      if (!tenant) return { status: 404, body: { code: 'TENANT_NOT_FOUND' } };
      const b = body as Record<string, unknown>;
      Object.assign(tenant, b, { updatedAt: now() });
      return ok(tenant);
    }

    const retryPath = path.match(/^\/v1\/tenants\/([^/]+)\/retry-provisioning$/);
    if (method === 'POST' && retryPath) {
      const tenant = MOCK_TENANTS_DB.find((t) => t['id'] === retryPath[1]);
      if (!tenant) return { status: 404, body: { code: 'TENANT_NOT_FOUND' } };
      tenant['status'] = 'provisioning';
      tenant['updatedAt'] = now();
      scheduleMockActivation(retryPath[1]);
      return {
        status: 202,
        body: { tenantId: retryPath[1], status: 'provisioning', queuedAt: now() },
      };
    }

    // ── Credenciales fleet_admin (FGA-90 / FGA-93 / M18) ──────────────────────
    // Lookup entregado por backend: lista en snake_case, sin act-as (GET de sólo
    // lectura). Tenant inexistente → 404 ACT_AS_TENANT_NOT_FOUND; sin admin → items:[].
    const fleetAdminsPath = path.match(/^\/v1\/admin\/tenants\/([^/?]+)\/fleet-admins$/);
    if (method === 'GET' && fleetAdminsPath) {
      const tenant = MOCK_TENANTS_DB.find((t) => t['id'] === fleetAdminsPath[1]);
      if (!tenant) return { status: 404, body: { code: 'ACT_AS_TENANT_NOT_FOUND' } };
      return ok({
        items: [
          {
            user_id: `fadmin-${fleetAdminsPath[1]}`,
            email: `admin@${tenant['subdomain']}.com`,
            full_name: `Administrador de ${tenant['commercialName']}`,
            is_active: tenant['status'] !== 'terminated',
          },
        ],
      });
    }

    const resetPwPath = path.match(/^\/v1\/admin\/users\/([^/?]+)\/reset-password$/);
    if (method === 'POST' && resetPwPath) {
      return ok({
        user_id: resetPwPath[1],
        temp_password: genTempPassword(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        must_change_password: true,
      });
    }

    // ── Acceso a la app del conductor (FGA-91 + ADMIN_FLEET / M18) ────────────
    // app-access = alta + reset; reset-password = solo reset. Ambos multi-rol
    // (SUPER_ADMIN act-as o ADMIN_FLEET directo); el mock no distingue headers.
    const driverCredPath = path.match(
      /^\/v1\/admin\/drivers\/([^/?]+)\/(app-access|reset-password)$/,
    );
    if (method === 'POST' && driverCredPath) {
      return ok({
        user_id: crypto.randomUUID(),
        temp_password: genTempPassword(),
        expires_at: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        must_change_password: true,
        driver_id: driverCredPath[1],
      });
    }

    // ── Files ─────────────────────────────────────────────────────────────────
    if (method === 'POST' && path === '/v1/files') {
      // id uuid: algunos contratos (p.ej. topup_evidence en FGA-18) validan
      // `evidenceFileId` como uuid antes de enviar la recarga.
      const fileId = crypto.randomUUID();
      const purpose =
        body instanceof FormData
          ? String(body.get('purpose') ?? 'vehicle_document')
          : typeof body?.['purpose'] === 'string'
            ? body['purpose']
            : 'vehicle_document';
      return {
        status: 201,
        body: {
          id: fileId,
          sha256: 'mock-sha256',
          sizeBytes: 24568,
          mimeType: 'application/pdf',
          originalFilename: 'document.pdf',
          purpose,
          uploadedAt: now(),
          downloadUrl: `/v1/files/${fileId}/download?t=mock-download-token`,
          downloadExpiresAt: new Date(Date.now() + 300_000).toISOString(),
        },
      };
    }

    // ── Geofences (FGA-16 / M05) ──────────────────────────────────────────────
    if (method === 'POST' && path === '/v1/geofences/import') {
      return {
        status: 201,
        body: makeGeofence({
          name: String(body?.['name'] ?? 'Geocerca importada'),
          categoryCode: String(body?.['category'] ?? 'operational'),
        }),
      };
    }
    if (method === 'POST' && path === '/v1/geofences') {
      return {
        status: 201,
        body: makeGeofence({
          name: String(body?.['name'] ?? 'Geocerca'),
          categoryCode: String(body?.['category'] ?? 'operational'),
          description: body?.['description'] ?? null,
          geometry: body?.['geometry'],
          effectiveFrom: body?.['effectiveFrom'] ?? null,
          effectiveTo: body?.['effectiveTo'] ?? null,
          validityWindow: body?.['validityWindow'] ?? null,
          isActive: body?.['isActive'] ?? true,
        }),
      };
    }
    const geoId = path.match(/^\/v1\/geofences\/([^/?]+)$/);
    if (method === 'GET' && path.startsWith('/v1/geofences') && !geoId) {
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      let items = GEOFENCES;
      const cat = qs.get('category');
      if (cat) items = items.filter((g) => g['categoryCode'] === cat);
      const active = qs.get('isActive');
      if (active !== null) items = items.filter((g) => String(g['isActive']) === active);
      const q = qs.get('q');
      if (q) items = items.filter((g) => String(g['name']).toLowerCase().includes(q.toLowerCase()));
      const page = Number(qs.get('page') ?? 1);
      const pageSize = Number(qs.get('pageSize') ?? 20);
      return ok({ items, page, pageSize, total: items.length });
    }
    if (method === 'GET' && geoId) {
      return ok(GEOFENCES.find((g) => g['id'] === geoId[1]) ?? makeGeofence({ id: geoId[1] }));
    }
    if (method === 'PATCH' && geoId) {
      const base = GEOFENCES.find((g) => g['id'] === geoId[1]) ?? makeGeofence({ id: geoId[1] });
      return ok({ ...base, ...body, updatedAt: now() });
    }
    if (method === 'DELETE' && geoId) {
      return { status: 204, body: null };
    }

    // ── Catálogos (combustibles / grupos) ─────────────────────────────────────
    if (method === 'GET' && path.startsWith('/v1/fuel-types')) {
      return ok({ fuelTypes: FUEL_TYPES });
    }
    const grpId = path.match(/^\/v1\/vehicle-groups\/([^/?]+)$/);
    if (method === 'GET' && grpId) {
      return ok(VEHICLE_GROUPS.find((g) => g.id === grpId[1]) ?? { id: grpId[1], name: 'Grupo' });
    }
    if (method === 'GET' && path.startsWith('/v1/vehicle-groups')) {
      return ok({ items: VEHICLE_GROUPS, nextCursor: null });
    }

    // ── Dispatch authorize / override (FGA-21 / M04) ──────────────────────────
    const dispatchAuthPath = path.match(/^\/v1\/dispatches\/([^/?]+)\/authorize$/);
    if (method === 'POST' && dispatchAuthPath) {
      const id = dispatchAuthPath[1];
      const outcome = DISPATCH_AUTH_OUTCOMES[id];
      if (outcome) return outcome;
      return ok(makeAuthorizeOutput(id, Number(body?.['volume'] ?? 0), false));
    }
    const dispatchOverridePath = path.match(/^\/v1\/dispatches\/([^/?]+)\/override$/);
    if (method === 'POST' && dispatchOverridePath) {
      return ok(makeAuthorizeOutput(dispatchOverridePath[1], 40.5, true));
    }

    // ── Dispatch / reserva atómica (FGA-19 / M04) ─────────────────────────────
    const dispatchCancelPath = path.match(/^\/v1\/dispatches\/([^/?]+)\/cancel$/);
    if (method === 'POST' && dispatchCancelPath) {
      const dispatchId = dispatchCancelPath[1];
      // 2º intento sobre el mismo despacho → ya terminal (idempotencia).
      if (CANCELED_DISPATCHES.has(dispatchId)) {
        return {
          status: 409,
          body: {
            code: 'RESERVATION_TERMINAL_STATE',
            message: 'La reserva ya está en un estado terminal.',
          },
        };
      }
      CANCELED_DISPATCHES.add(dispatchId);
      const reason = (body?.['reason'] as string) ?? 'dispatch_canceled';
      return ok({
        dispatchId,
        status: 'canceled',
        reservation: {
          id: crypto.randomUUID(),
          status: 'released',
          releasedReason: reason,
          releasedAt: now(),
        },
      });
    }

    // ── Credit balances / top-ups (FGA-18 / M04) ──────────────────────────────
    if (method === 'GET' && path === '/v1/credit-balances/me') {
      return ok({
        currentAmount: 50000,
        reservedAmount: 0,
        availableAmount: 50000,
        currencyCode: 'GTQ',
      });
    }
    const topupsPath = path.match(/^\/v1\/credit-balances\/([^/?]+)\/topups(?:\?.*)?$/);
    if (method === 'POST' && topupsPath) {
      return { status: 201, body: makeTopUp(body ?? {}) };
    }
    if (method === 'GET' && topupsPath) {
      return ok({ items: TOPUPS, nextCursor: null });
    }

    // ── Reservas de despacho por vehículo (FGA-32 / M15 — Endpoint A) ──────────
    // POST está DOCUMENTADO; GET listado y cancel son @contract-assumed FGA-32.
    const resvCancelPath = path.match(/^\/v1\/credit-reservations\/([^/?]+)\/cancel$/);
    if (method === 'POST' && resvCancelPath) {
      // @contract-assumed FGA-32
      const resvId = resvCancelPath[1];
      const existing = VEHICLE_RESERVATIONS.find((r) => r['reservationId'] === resvId);
      if (existing && existing['status'] === 'canceled') {
        return {
          status: 409,
          body: { code: 'RESERVATION_ALREADY_CANCELED', message: 'La reserva ya fue cancelada.' },
        };
      }
      const base = existing ?? makeVehicleReservation({ reservationId: resvId });
      base['status'] = 'canceled';
      return ok({ ...base, status: 'canceled' });
    }
    if (method === 'POST' && path === '/v1/credit-reservations') {
      const driverId = body?.['driverId'] as string | undefined;
      const vehicleId = body?.['vehicleId'] as string;
      // FGA-32b: sin driverId, el backend deriva la asignación; si no hay → 422.
      if (!driverId && vehicleId === VEHICLE_NO_DRIVER) {
        return {
          status: 422,
          body: {
            code: 'NO_ACTIVE_DRIVER_ASSIGNMENT',
            message: 'El vehículo no tiene un conductor asignado activo.',
          },
        };
      }
      if (driverId === DRIVER_WITH_ACTIVE_RESERVATION) {
        return {
          status: 409,
          body: {
            code: 'DRIVER_HAS_ACTIVE_RESERVATION',
            message: 'El conductor ya tiene una reserva activa (RN-MVP-12).',
            driverId,
            activeReservation: {
              reservationId: '019ef6aa-0000-7000-8000-000000000001',
              vehicleId: RESV_SEED_VEHICLE,
              vehiclePlate: 'P-123ABC',
              expiresAt: new Date(Date.now() + 12 * 60 * 60_000).toISOString(),
            },
          },
        };
      }
      if (vehicleId === VEHICLE_NO_RULE) {
        return {
          status: 422,
          body: {
            code: 'NO_APPLICABLE_RULE',
            message: 'Ninguna regla de crédito vigente aplica a este vehículo.',
            vehicleId,
            resolvedFrom: 'none',
            fix: { method: 'POST', path: '/v1/credit-rules', field: null },
          },
        };
      }
      // FGA-32c §3 #7 — el caso reportado: la regla no tiene `maxPricePerUnit`.
      if (vehicleId === VEHICLE_NO_PRICE) {
        return {
          status: 422,
          body: {
            code: 'NO_REFERENCE_PRICE',
            message:
              'La regla de crédito «50 gal Regular» no define un precio máximo por unidad. Sin ese techo no hay con qué valorar el monto a retener para una reserva que no está atada a una estación.',
            rule: NO_PRICE_RULE_REF,
            missingField: 'maxPricePerUnit',
            fix: {
              method: 'PATCH',
              path: `/v1/credit-rules/${NO_PRICE_RULE_REF.id}`,
              field: 'maxPricePerUnit',
            },
          },
        };
      }
      // FGA-32c §3 #11 — hay dinero, falta cupo (antes se confundía con saldo).
      if (vehicleId === VEHICLE_LIMIT_EXCEEDED) {
        const requested = Number(body?.['maxVolume'] ?? 250);
        return {
          status: 409,
          body: {
            code: 'CREDIT_LIMIT_EXCEEDED',
            message:
              'La reserva supera el volumen máximo diario de la regla de crédito «50 gal Regular».',
            limit: 'daily_volume',
            ruleField: 'dailyLimitVolume',
            requested,
            limitValue: 5,
            used: 0,
            available: 5,
            unit: (body?.['unit'] as string) ?? 'gallon',
            currencyCode: (body?.['currencyCode'] as string) ?? 'GTQ',
            rule: NO_PRICE_RULE_REF,
            fix: {
              method: 'PATCH',
              path: `/v1/credit-rules/${NO_PRICE_RULE_REF.id}`,
              field: 'dailyLimitVolume',
            },
          },
        };
      }
      // FGA-32c §3 #10 — la flota nunca recibió crédito.
      if (vehicleId === VEHICLE_WALLET_EMPTY) {
        return {
          status: 409,
          body: {
            code: 'WALLET_NOT_FUNDED',
            message: 'La flota no tiene crédito cargado. Un SUPER_ADMIN debe hacer una recarga.',
            fix: {
              method: 'POST',
              path: '/v1/credit-balances/019ef600-0000-7000-8000-000000000001/topups',
              field: null,
            },
          },
        };
      }
      if (vehicleId === VEHICLE_INSUFFICIENT_BALANCE) {
        return {
          status: 409,
          body: {
            code: 'INSUFFICIENT_BALANCE',
            message: 'El saldo de crédito de la flota no alcanza para esta reserva.',
            requestedAmount: Number(body?.['maxVolume'] ?? 10) * 37.8541,
            availableAmount: 120.5,
            currencyCode: (body?.['currencyCode'] as string) ?? 'GTQ',
            fix: {
              method: 'POST',
              path: '/v1/credit-balances/019ef600-0000-7000-8000-000000000001/topups',
              field: null,
            },
          },
        };
      }
      const created = makeVehicleReservation(body ?? {});
      VEHICLE_RESERVATIONS.push(created);
      return { status: 201, body: created };
    }
    if (
      method === 'GET' &&
      (path === '/v1/credit-reservations' || path.startsWith('/v1/credit-reservations?'))
    ) {
      // @contract-assumed FGA-32
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      let items = [...VEHICLE_RESERVATIONS];
      const vehicleId = qs.get('vehicleId');
      if (vehicleId) items = items.filter((r) => r['vehicleId'] === vehicleId);
      const driverId = qs.get('driverId');
      if (driverId) items = items.filter((r) => r['driverId'] === driverId);
      const status = qs.get('status');
      if (status && status !== 'all') items = items.filter((r) => r['status'] === status);
      return ok({ reservations: items });
    }

    // ── Credit rules (FGA-17 / M04) ───────────────────────────────────────────
    // ⚠️ `/effective` se resuelve ANTES que `/:id` (ruta literal).
    if (method === 'GET' && path.startsWith('/v1/credit-rules/effective')) {
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      const vehicleId = qs.get('vehicleId') ?? '';
      if (vehicleId === VEHICLE_NO_RULE) {
        return ok({
          rule: null,
          resolvedFrom: 'none',
          groupId: null,
          currentConsumption: {
            dailyAmount: 0,
            dailyVolume: 0,
            monthlyAmount: 0,
            monthlyVolume: 0,
          },
        });
      }
      // FGA-32c §2: regla vigente pero SIN techo de precio → el form se bloquea
      // con CTA a editarla, en vez de dejar que el usuario choque contra el 422.
      if (vehicleId === VEHICLE_NO_PRICE) {
        return ok({
          rule: CREDIT_RULES[2],
          resolvedFrom: 'fleet',
          groupId: null,
          currentConsumption: {
            dailyAmount: 0,
            dailyVolume: 0,
            monthlyAmount: 0,
            monthlyVolume: 0,
          },
        });
      }
      return ok({
        rule: CREDIT_RULES[1],
        resolvedFrom: 'group',
        groupId: '019ef777-0002-7000-8000-000000000abc',
        currentConsumption: {
          dailyAmount: 0,
          dailyVolume: 18,
          monthlyAmount: 0,
          monthlyVolume: 240,
        },
      });
    }
    if (
      method === 'GET' &&
      path.startsWith('/v1/credit-rules') &&
      !path.match(/\/credit-rules\//)
    ) {
      const qs = new URLSearchParams(path.includes('?') ? path.slice(path.indexOf('?')) : '');
      let items = [...CREDIT_RULES];
      const level = qs.get('level');
      if (level) items = items.filter((r) => r['scopeType'] === level);
      // Espeja el `contains` + `mode:'insensitive'` sobre `name` del backend
      // (FGA-150). Sin esto el filtro funciona en dev y falla contra la API real.
      const search = qs.get('search')?.trim().toLowerCase();
      if (search)
        items = items.filter((r) =>
          String(r['name'] ?? '')
            .toLowerCase()
            .includes(search),
        );
      const scopeId = qs.get('scopeId');
      if (scopeId) items = items.filter((r) => r['scopeId'] === scopeId);
      const active = qs.get('active');
      if (active !== null) items = items.filter((r) => String(r['isActive']) === active);
      return ok({ items, nextCursor: null });
    }
    if (method === 'POST' && path === '/v1/credit-rules') {
      return { status: 201, body: makeCreditRule(body ?? {}) };
    }
    const crId = path.match(/^\/v1\/credit-rules\/([^/?]+)$/);
    if (method === 'GET' && crId) {
      return ok(CREDIT_RULES.find((r) => r['id'] === crId[1]) ?? makeCreditRule({ id: crId[1] }));
    }
    if (method === 'PATCH' && crId) {
      const base = CREDIT_RULES.find((r) => r['id'] === crId[1]) ?? makeCreditRule({ id: crId[1] });
      return ok({ ...base, ...body, updatedAt: now() });
    }
    if (method === 'DELETE' && crId) {
      return { status: 204, body: null };
    }

    return null;
  }
}

function ok(body: unknown): MockResult {
  return { status: 200, body };
}

function now(): string {
  return new Date().toISOString();
}
